import { currencyFloatToInteger, integerToCurrencyFloat, PriceList } from '@acme/b2b/pricing';
import { AddonTypeSelectorComponentModule } from '@b2b/common/components/addon-type-selector';
import { LimitsBannerComponentModule } from '@b2b/common/components/limits-banner';
import { NoticesComponentModule } from '@b2b/common/components/notices';
import { TenantPolicyTermsComponentModule } from '@b2b/common/components/tenant-policy-terms';
import { TicketTypeSelectorComponentModule } from '@b2b/common/components/ticket-type-selector';
import {
  normalizeQuantity,
  isDiscountedItem,
  isMembershipConversion,
  isEventEditable,
  isAddon,
  isFee,
  isEventItemType,
  isInventoryItemType,
  hasAllEventItemsWithQuantity,
} from '../utils/order-validation.utils';
import {
  formatEventDates,
  processEventState,
  getDefaultEmailLanguage,
  transformEventToWillCallOrderItemsByEvent,
  applyResellerDiscounts,
  applyBasePriceDisplay,
  buildFormItemsForOrder,
} from '../utils/order-processing.utils';
import {
  getMembershipIdFromOrder,
  getAccountCategoryIdFromOrder,
  extractAppliedCoupons,
  applyDiscountsToEventItems,
  calculateOriginalSubtotal,
  addMembershipIdToEventItems,
  addAccountCategoryIdToEventItems,
  formatPrimaryCardholderName,
  resolveTicketDiscount,
} from '../../utils';
import { PaymentProcessError } from '@b2b/common/directives/payment/types';
import { NoticeFactory } from '@b2b/common/factories/notice';
import { TrustAsHtmlFilterModule, parseUrl, ParseUrlFilterModule } from '@b2b/common/filters';
import { ThemesData } from '@b2b/common/resolvers';
import { safeApply } from '@b2b/common/rxjs';
import { ModalService, ModalServiceModule } from '@b2b/common/services';
import { AddressService, AddressServiceModule } from '@b2b/common/services/address';
import {
  OrderEditSalesRulesService,
  OrderEditSalesRulesServiceModule,
} from '@b2b/common/services/salesRules/order-edit-sales-rules.service';
import { LocationService, LocationServiceModule } from '@b2b/common/services/address/location.service';
import {
  OrderChargesResultVo,
  OrderUpdateInputSchema,
  UpdateOrderItemInput,
} from '@b2b/common/services/api/ordersApi/types/api';
import { PartnerDetail } from '@b2b/common/services/api/partnersApi/types';
import { ApiService } from '@b2b/common/services/api/types';
import { CalendarService } from '@b2b/common/services/calendar';
import {
  CheckoutProcessService,
  CheckoutProcessServiceModule,
} from '@b2b/common/services/checkout/checkoutProcess.service';
import { OrderEditCoreData, OrderEditData, SecureOrderEditProps } from '@b2b/common/services/checkout/edit-order.types';
import { isACMECORSCheckoutError } from '@b2b/common/services/checkout/types';
import { PaymentService, PaymentServiceModule } from '@b2b/common/services/payment/payment.service';
import { PermissionsService } from '@b2b/common/services/permissions';
import { ThemesHelperService } from '@b2b/common/services/themesHelper';
import { ValidateService } from '@b2b/common/services/validate';
import { getGiftCardRedemptionsTotal } from '@b2b/common/utils/gift-card';
import { allSettled } from '@b2b/common/utils/all-settled';
import { ensureArray } from '@b2b/common/utils/coerce-array';
import { attachMeta } from '@b2b/common/utils/meta';
import {
  ACMEResponse,
  ContentHeaderProps,
  extractACMEPayload,
  LooseObject,
  TODO,
  UIBModalService,
} from '@b2b/types/core';
import { GiftCardRedemption } from '@b2b/types/cart';
import { EventOrderForm, EventVo } from '@b2b/types/event';
import { Order, OrderEditModel, OrderEventItem, OrderEventItems, OrderEditEventItem } from '@b2b/types/order';
import { EventInstancesCouponAllPricelist } from '@b2b/common/services/api/eventApi/types';
import { CouponVo } from '@b2b/types/order/api/will-call-order-detail.type';
import { WillCallOrderItemsByEventVo } from '@b2b/types/order/api/will-call-order-detail.type';
import uiRouter, { StateService } from '@uirouter/angularjs';
import angular, { IFilterService, IPromise, IQService, ISCEService, IScope, ITimeoutService } from 'angular';
import {
  createPrivateEventModalConfig,
  CreatePrivateEventModalControllerModule,
} from '../../../programs/modals/createPrivateEvent/controllers/CreatePrivateEventController';
import {
  OrdersCheckoutAddEventModalControllerModule,
  OrdersCheckoutAddEventModalData,
} from '../../checkout/controllers/OrdersCheckoutAddEventModalController';
import { OrdersModalsChangeOrgControllerModule } from '../../modals/changeOrg/controllers/OrdersModalsChangeOrgController';
import { excludePseudoEventItems, partitionAddonsAndFeesLast } from '../../utils';
import { ExistingOrderEventTableComponentModule } from '../components/existing-order-event-table/existing-order-event-table.component';
import { NewOrderEventTableComponentModule } from '../components/new-order-event-table/new-order-event-table.component';
import { ExistingOrderAddonsAndFeesComponentModule } from '../components/existing-order-addons-and-fees/existing-order-addons-and-fees.component';
import { OrderEventHeaderComponentModule } from '../components/order-event-header/order-event-header.component';
import { OrderSummaryComponentModule } from '../components/order-summary/order-summary.component';
import {
  DEFAULT_EMAIL_LANGUAGE_CODE,
  EmailLanguage,
  EmailOption,
  EmailOptions,
  PersonTypeKey,
  pickDiscountMeta,
  ProcessedOrderEventItems,
  SelectedItemType,
} from './OrdersEditController.types';

import { isPrimaryCardholder, MembershipCardholder, MembershipDetails } from '@b2b/types/membership';
import { deduplicateOrderFormsByDisplaySettings } from '../../../cart/components/cart-forms/utils/form-filters.utils';
import {
  OrderFormWithResponse,
  FormResponseRecord,
  FormResponseDataRecord,
  FormResponseRecordWithFormUUID,
} from '../../../cart/components/cart-forms/types';
import {
  CartFormsService,
  CartOrderFormWithRequired,
} from '../../../cart/components/cart-forms/services/cart-forms.service';
import { FormsSummariesListItem } from '@b2b/types/forms';

OrdersEditController.$inject = [
  '$scope',
  '$q',
  '$sce',
  '$state',
  '$filter',
  '$timeout',
  '$uibModal',
  'ModalService',
  'uuid2',
  'ApiService',
  'OrderService',
  'CalendarService',
  'PermissionsService',
  'HelpersService',
  'ThemesHelperService',
  'ValidateService',
  'NoticeFactory',
  'OrderData',
  'OrderFormsData',
  'ThemesData',
  'UserPreferencesService',
  'CheckoutProcessService',
  'LocationService',
  'PaymentService',
  'AddressService',
  'OrderEditSalesRulesService',
  'CartFormsService',
];

function OrdersEditController(
  $scope: IScope & { ordersEdit: TODO; stateTimer?: IPromise<void> },
  $q: IQService,
  $sce: ISCEService,
  $state: StateService,
  $filter: IFilterService,
  $timeout: ITimeoutService,
  $uibModal: UIBModalService,
  modalService: ModalService,
  uuid2: TODO,
  ApiService: ApiService,
  OrderService: TODO,
  CalendarService: CalendarService,
  PermissionsService: PermissionsService,
  HelpersService: TODO,
  ThemesHelperService: ThemesHelperService,
  ValidateService: ValidateService,
  NoticeFactory: NoticeFactory,
  OrderData: { data: ACMEResponse<Order> },
  OrdersFormsData: { data: ACMEResponse<{ list: FormsSummariesListItem[] }> },
  ThemesData: { data: ACMEResponse<ThemesData> },
  UserPreferencesService: TODO,
  CheckoutProcessService: CheckoutProcessService,
  LocationService: LocationService,
  paymentService: PaymentService,
  addressService: AddressService,
  orderEditSalesRulesService: OrderEditSalesRulesService,
  cartFormsService: CartFormsService,
) {
  const ctrl = {
    requiresCardData: false,

    orderSaveComplete: false,

    originalOrder: angular.copy(OrderData.data.payload),

    model: {} as OrderEditModel,

    removedForms: [] as OrderFormWithResponse[],

    addonsAndFeesEvent: null as OrderEventItems | null,

    /** View IDs to watch for navigation away (used by promptSave directive) */
    viewIds: '["application.orders.edit"]',

    appliedCoupons: [] as CouponVo[],
    eventCoupons: [] as EventInstancesCouponAllPricelist[],

    requestUUID: false as string | false,

    billingSameAsOrderContact: false,
    selectedOrgContact: {} as TODO,

    isEnabledCORSCheckout: false,
    corsCheckoutCreditCardMessage: `Payment will be securely collected when you select "Take Payment & Update Order"`,

    orderDetails: {
      columns: 1,
      data: 'ordersEdit.model',
      include: ['orderNumber'],
    } as {
      columns: number;
      data: string;
      include: string[];
      filters?: {
        membershipId: (v: any) => any;
      };
    },

    accountDetails: {
      columns: 1,
      data: 'ordersEdit.model',
      sorting: true,
      include: ['accountName'],
      mappings: {
        accountName: 'Organization Name',
      },
    },

    orderLanguages: {
      columns: 1,
      data: 'ordersEdit.model',
      include: ['preferredLanguage'],
    },

    orgOrderContactDetails: {
      columns: 1,
      data: 'ordersEdit.selectedOrgContact',
      include: ['email', 'phoneNumber'],
    },

    orderContactDetails: {
      columns: 1,
      data: 'ordersEdit.model',
      include: ['contactFirstName', 'contactLastName', 'email', 'phoneNumber'],
    },

    billingDetails: {
      columns: 1,
      data: 'ordersEdit.model',
      include: ['billingFirstName', 'billingLastName', 'billingEmail', 'billingPhoneNumber'],
      mappings: {
        billingFirstName: 'First Name',
        billingLastName: 'Last Name',
        billingEmail: 'Email',
        billingPhoneNumber: 'Phone Number',
      },
    },

    membershipsDetails: {
      columns: 1,
      hideLabels: true,
      include: ['membershipName', 'membershipPrimaryCardholder', 'membershipExpirationDate'],
    },

    emailLanguages: [
      { code: 'en-nz', name: 'English (New Zealand)' },
      { code: 'en-us', name: 'English (United States)' },
      { code: 'fr-ca', name: 'French (Canada)' },
      { code: 'es-mx', name: 'Spanish (Mexico)' },
    ],

    emailOptions: false,
    sendEmail: true,
    lastOrg: {} as TODO,
    summary: null as OrderChargesResultVo | null,
    events: {} as Record<string, EventVo>,
    addons: {} as Record<string, TODO>,
    personTypes: {} as Record<PersonTypeKey, TODO>,
    orderItems: {} as Record<string, UpdateOrderItemInput>,
    accountPaymentPolicy: {} as TODO,
    accountCategoryRelation: null as PartnerDetail['accountCategoryRelation'] | null,
    isDiscounted: false,
    ticketDelivery: '',
    isSaving: false,
    isLoaded: false,
    isProcessing: false,
    checkingLimits: false,
    notice: new NoticeFactory(),
    getItemType: OrderService.getItemType,
    hasPurchasedFeeItems: function (items: Array<{ quantity?: number; resolvedQuantity?: number }>) {
      return Array.isArray(items) && items.some((item) => (item.quantity || 0) > 0 || (item.resolvedQuantity || 0) > 0);
    },
    themes: {},
    enableCSRToggle: false,
    enableSalesTax: false,
    isTaxExempt: false,
    dateFormat: UserPreferencesService.getDateFormat('shortDate', 'dateFormat'),
    hourFormat: UserPreferencesService.getDateFormat('standardTime', 'hourFormat'),
    hasMembershipConversion: false,
    validateEmail: true,
    hasEmailError: false,
    emailOption: '' as EmailOption,

    addOnQuantity: 0,
    quantity: 0,
    total: 0,
    payment: {
      type: '',
      deposit: { minAmount: 0, percent: 0 },
    },

    giftCardRedemptions: [] as GiftCardRedemption[],
    giftCardRedemptionsTotal: 0,

    forms: [] as OrderFormWithResponse[],
    originalOrderForms: [] as FormResponseDataRecord[],
    originalOrderFormUuids: new Set<string>(),

    init: function () {
      paymentService.processData$
        .pipe(
          safeApply($scope, {
            next: ({ chargeAmount, paymentMethod }) => {
              ctrl.requiresCardData = paymentMethod === 'CreditCard' && chargeAmount > 0;
            },
          }),
        )
        .subscribe();

      if (PermissionsService.check('CSR')) {
        ctrl.enableCSRToggle = true;
      }

      const params = ctrl.getStateParams();

      if (params.customerId) {
        ctrl.model.customerId = params.customerId;
      }

      if (params.accountId && PermissionsService.check('ACCT_ORDERS_UPDATE')) {
        ctrl.model.accountId = params.accountId;
        ctrl.ticketDelivery = 'willCall';
        ctrl.getPayment(ctrl.model.accountId);
      } else if (PermissionsService.check('ORDER_UPDATE')) {
        ctrl.ticketDelivery = 'print';
        ctrl.model.accountId = null;
      } else {
        $state.go('home.default', {}, { notify: true });
      }

      $scope.$emit('Application::contentTitleChange', {
        title: 'Edit Order',
        buttonLeft: {
          label: 'Cancel',
          action: () => {
            ctrl.cancel();
          },
        },
      } as ContentHeaderProps);

      if (OrderData.data && OrderData.data.success) {
        if (OrderData.data.payload && OrderData.data.payload.accountId) {
          if (!PermissionsService.check('ACCT_ORDERS_UPDATE')) {
            $state.go('home.default', {}, { notify: true });
          }
          ctrl.getPayment(OrderData.data.payload.accountId);
          ctrl.getPartner(OrderData.data.payload.accountId);
        } else {
          if (!PermissionsService.check('ORDER_UPDATE')) {
            $state.go('home.default', {}, { notify: true });
          }
        }
        // first get order event details, then process the order itself
        ctrl.getOrderEventDetails(OrderData.data.payload.orderId);
      } else {
        // noop
      }

      ThemesHelperService.resetModel();

      if (ThemesData.data && ThemesData.data.success) {
        ctrl.initThemes(ThemesData);
      }

      // Clear any existing form responses from session storage
      // to avoid accidentally populating forms with stale data from a previous order edit session
      cartFormsService.clearFormResponses();

      $scope.$on('Payment::Complete', ctrl.onPaymentComplete);
      $scope.$on('Payment::Error', ctrl.onPaymentError);
      $scope.$on('ContactsTypeahead::NoResults', ctrl.onContactsTypeaheadNoResults);
      $scope.$on('ContactsTypeahead::ContactNotFound', ctrl.onContactsTypeaheadContactNotFound);

      $scope.$on('$destroy', $scope.$watch('ordersEdit.selectedOrgContact', ctrl.onSelectedOrgContactUpdate));
    },

    initThemes: function (response) {
      ThemesHelperService.setModel(response.data.payload);
      CheckoutProcessService.initACMECheckout();

      ctrl.themes = {
        policyTerms: ThemesHelperService.getHTMLVariable('checkout-process-page-terms'),
      };

      ctrl.enableSalesTax = ThemesHelperService.getFeatureFlag('enableSalesTax');

      ctrl.isEnabledCORSCheckout = CheckoutProcessService.isEnabledCORSCheckout();
    },

    getStateParams() {
      return {
        accountId: ($state.params.accountId ? Number($state.params.accountId) : null) as number | null,
        customerId: ($state.params.customerId ?? null) as string | null,
      };
    },

    isOrganizationOrder: function () {
      return !!ctrl.model.accountId;
    },

    getOrderEventDetails: function (orderId: string) {
      ApiService.orders
        .events(orderId)
        .then((response) => {
          // process the order after getting the order event details
          ctrl.processOrderEvents(response.data.payload);

          ctrl.initOrderData();

          ctrl.initOrderFormsData();

          ctrl.loadResellerEventDetails();

          ctrl.loadMembershipDetails();

          ctrl.extractAppliedCoupons();

          ctrl.fetchAvailableCoupons();

          ctrl.getOrderAddonDetails().then(() => {
            ctrl.initOrderAddonsData();
            ctrl.selectEmailOption();
          });
        })
        .catch((response) => {
          ctrl.notice.showErrorMessages([{ message: response.data.error }]);
        });
    },

    updateAttachedEventMeta: function () {
      for (const orderEvent of ctrl.model.eventItems) {
        if (!orderEvent.eventId) {
          continue;
        }

        if (!ctrl.events[orderEvent.eventId]) {
          continue;
        }

        const event = ctrl.events[orderEvent.eventId];
        attachMeta(orderEvent, {
          morePrices: ctrl.getRemainingPricesForEvent(event, orderEvent),
          moreAddOns: ctrl.getRemainingAddOnsForEvent(event, orderEvent),
        });
      }
    },

    getOrderAddonDetails: function () {
      const q = $q.defer();
      const queue: TODO[] = [];

      for (let i = 0, ii = ctrl.model.eventItems.length; i < ii; i++) {
        for (let j = 0, jj = ctrl.model.eventItems[i].items.length; j < jj; j++) {
          const item = ctrl.model.eventItems[i].items[j];
          const itemTypeId = item.itemTypeId;
          // Load addon details only for items in the Addons event or ComboInventory items
          if ((ctrl.model.eventItems[i].eventId === 'Addons' || item.itemType === 'ComboInventory') && itemTypeId) {
            queue.push(ApiService.inventory.detail(itemTypeId));
          }
        }
      }

      allSettled($q, queue).then((response) => {
        let next;

        while ((next = response.pop())) {
          if (next.data.error) {
          } else {
            ctrl.addons[next.data.payload.id] = next.data.payload;
          }
        }

        q.resolve(ctrl.addons);
      });

      return q.promise;
    },

    getPayment: function (accountId: number) {
      ApiService.partners.payments
        .account(accountId)
        .then((response) => {
          ctrl.accountPaymentPolicy = response.data.payload;
        })
        .catch((response) => {
          ctrl.notice.showErrorMessages([{ message: response.data.error }]);
        });
    },

    getPartner: function (accountId: number) {
      ApiService.partners
        .detail(accountId)
        .then((response) => {
          const partner = response.data.payload;
          ctrl.accountCategoryRelation = partner.accountCategoryRelation;
        })
        .catch((response: TODO) => {
          ctrl.notice.showErrorMessages([{ message: response.data.error }]);
        });
    },

    getDuplicateOrder: function () {
      const uuid = ctrl.requestUUID ? ctrl.requestUUID : '';

      ApiService.system
        .uuidObjectRegistry({ uuid })
        .then((response) => ctrl.success.getDuplicateOrder(response))
        .catch((response) => ctrl.error.getDuplicateOrder(response));
    },

    initOrderAddonsData: function () {
      for (const orderEvent of ctrl.model.eventItems) {
        for (const eventItem of orderEvent.items as LooseObject<OrderEventItem>[]) {
          if (ctrl.isInventoryItemType(eventItem.itemType)) {
            eventItem.resolvedQuantity = eventItem.quantity;

            if (orderEvent.eventId && !ctrl.eventHasAddOns(orderEvent.eventId)) {
              const key = ctrl.createPersonTypeKey({
                eventId: orderEvent.eventId,
                itemTypeId: eventItem.itemTypeId,
                orderItemId: eventItem.orderItemId,
              });

              ctrl.personTypes[key] = ctrl.addons[eventItem.itemTypeId];
              eventItem.salesChannels = ctrl.personTypes[key].salesChannels;

              if (!Array.isArray(eventItem.salesChannels)) {
                eventItem.salesChannels = [];
              }
            }
          }
        }
      }
    },

    initOrderData: function () {
      const payload = OrderData.data.payload;
      payload.eventItems = partitionAddonsAndFeesLast(payload.eventItems);
      ctrl.model = ctrl.processOrder(OrderData.data.payload) as TODO;

      // Tax-exempt orders never charge tax, so newly added items must not contribute
      // tax to the totals. Captured from the original order (GET /willCall/orders/{orderId}).
      ctrl.isTaxExempt = !!payload.isTaxExempt;

      // Normalize the original order's items to base pricing too, so the per-row
      // difference comparisons (calculateOriginalSubtotal) stay consistent with the
      // now-base current items, while preserving their tax-inclusive prices.
      for (const event of ctrl.originalOrder.eventItems ?? []) {
        for (const item of event.items) {
          applyBasePriceDisplay(item, ctrl.enableSalesTax);
        }
      }

      ctrl.refreshFormsFromEvents();

      if (ctrl.model.membershipId) {
        ctrl.isDiscounted = true;
        ctrl.orderDetails.filters = {
          membershipId: (v) => {
            return v && v !== 'null' && v !== null ? v : false;
          },
        };
        ctrl.orderDetails.include = ctrl.orderDetails.include.concat(['membershipId']);
      }

      ctrl.isLoaded = true;
      ctrl.addonsAndFeesEvent = ctrl.buildAddonsAndFeesEvent();
    },

    initOrderFormsData: function () {
      const orderId = ctrl.model.orderId;

      // Capture all original form UUIDs (including those without responses)
      ctrl.originalOrderFormUuids = new Set(
        OrdersFormsData.data.payload.list.map((formSummary) => `${formSummary.formId}-${formSummary.itemId}`),
      );

      const orderFormsDataList = OrdersFormsData.data.payload.list
        .filter((formSummary) => formSummary.formResponseId)
        .map((formSummary) => ApiService.orders.forms.response({ orderId, responseId: formSummary.formResponseId }));

      allSettled($q, orderFormsDataList).then((responses) => {
        const formResponses = responses.map((response) => response.data.payload);

        formResponses.forEach((response: FormResponseDataRecord) => {
          cartFormsService.saveFormResponse(
            `${response.formId}-${response.itemId}`,
            response.formResponseData as unknown as FormResponseRecordWithFormUUID,
          );
        });

        ctrl.originalOrderForms = formResponses as FormResponseDataRecord[];

        ctrl.forms.forEach((form) => {
          const response = formResponses.find((r) => form.uuid === `${r.formId}-${r.itemId}` && r.formResponseData);
          if (response) {
            form.response = response.formResponseData;
          }
        });
      });
    },

    resetModel: function () {
      ctrl.model.notes = '';
      ctrl.model.orderId = '';
      ctrl.model.orderItems = [];
      ctrl.model.noEmail = false;
      ctrl.model.payment = {};
      ctrl.model.address = {};
    },

    /**
     * Extract coupons that have already been applied to order items.
     * Populates ctrl.appliedCoupons with CouponVo objects from item.couponDetail.
     */
    extractAppliedCoupons: function () {
      ctrl.appliedCoupons = extractAppliedCoupons(ctrl.model.eventItems);
    },

    /**
     * Fetch available coupon price lists for all events in the order from the API.
     * Populates ctrl.eventCoupons with EventInstancesCouponAllPricelist objects.
     */
    fetchAvailableCoupons: function () {
      const eventIds = ctrl.model.eventItems
        .filter((event) => event.items.some((item) => item.itemType === 'Event'))
        .map((event) => event.eventId);

      return ApiService.event.coupon
        .priceLists({ eventIds: eventIds.join(','), validate: '1' })
        .then((response) => {
          ctrl.eventCoupons = response.data.payload;
          ctrl.updateEventItemDiscounts();
          ctrl.updateAttachedEventMeta();
        })
        .catch(() => {
          // noop
        });
    },

    /**
     * Loads membership details from the API and populates ctrl.model.memberships.
     * Fetches the primary cardholder info, formats the display name, and adds
     * a link to the membership detail page along with the expiration date.
     */
    loadMembershipDetails: function () {
      const membershipId = ctrl.getMembershipIdFromOrder(ctrl.model);

      if (!membershipId) {
        return;
      }

      ctrl.model.memberships = [];

      ApiService.membership
        .detail(membershipId)
        .then((response) => {
          const membership: MembershipDetails & { primaryCardholder?: MembershipCardholder } = response.data.payload;

          membership.primaryCardholder = membership.cardholders.find(isPrimaryCardholder);

          if (!membership.primaryCardholder) {
            return;
          }

          const membershipPrimaryCardholder = formatPrimaryCardholderName(membership.primaryCardholder);

          const membershipName = parseUrl(
            $state.href('application.membership.detail', { id: membership.id }),
            `${membership.membershipCategory}: ${membership.offeringName}`,
          );

          const membershipExpirationDate =
            'Expires: ' +
            CalendarService.toFormat(
              membership.membershipExpirationDate,
              UserPreferencesService.getDateFormat('shortDate', 'dateFormat'),
            );

          ctrl.model.memberships.push({
            membershipName,
            membershipPrimaryCardholder,
            membershipExpirationDate,
          });
        })
        .catch(() => {
          // noop
        });
    },

    /**
     * Loads reseller event details from the API for each event in the order.
     * Requires accountId on the order. Fires one request per eventId in parallel.
     *
     * For each event, matches reseller prices to order items by personType.id === item.itemTypeId
     * and applies discountId/discountName where not already set, on both:
     *  - ctrl.model.eventItems[].items[]
     *  - ctrl.events[eventId].priceList.prices[]
     */
    loadResellerEventDetails: function () {
      const accountId = ctrl.model.accountId;

      if (!accountId) {
        return;
      }

      const eventItems = excludePseudoEventItems(ctrl.model.eventItems);

      if (eventItems.length === 0) {
        return;
      }

      const requests = eventItems.map((eventItem) =>
        ApiService.partners.reseller
          .event({ accountId: Number(accountId), eventId: eventItem.eventId as string })
          .then((response) => {
            const resellerPrices: TODO[] = response.data.payload?.priceList?.prices ?? [];
            applyResellerDiscounts(eventItem, resellerPrices, ctrl.events);
          })
          .catch(() => {
            // noop - reseller details are optional
          }),
      );

      return $q.all(requests).then(() => {
        ctrl.updateAttachedEventMeta();
      });
    },

    getGiftCardAmountDue: function () {
      return Math.max(0, ctrl.getBalanceDue() - ctrl.giftCardRedemptionsTotal);
    },

    onGiftCardRedemptionsChange: function (redemptions: GiftCardRedemption[]) {
      ctrl.giftCardRedemptions = redemptions;
      ctrl.giftCardRedemptionsTotal = getGiftCardRedemptionsTotal(redemptions);
    },

    getOrderBalance: function () {
      return currencyFloatToInteger(ctrl.model.orderBalance || 0);
    },

    getOrderDifference: function () {
      let totalDifference = 0;

      // Iterate through ALL events (original + newly added). The order difference is the
      // change in real (tax-inclusive) money, so it uses the tax-inclusive prices on both
      // the current items and their original-order counterparts.
      for (const event of ctrl.model.eventItems) {
        for (const item of event.items) {
          if (!ctrl.isTotalableItem(event, item)) {
            continue;
          }
          const quantity = ctrl.normalizeQuantity(item.quantity ?? 0);
          const currentPrice = quantity * currencyFloatToInteger(ctrl.getItemPayablePrice(item));
          // For newly added items there is no original, so the original price will be 0.
          const originalPrice = currencyFloatToInteger(
            ctrl.calculateOriginalSubtotal(item as TODO, undefined, { useTaxInclusive: true }),
          );

          totalDifference += currentPrice - originalPrice;
        }
      }

      return integerToCurrencyFloat(totalDifference);
    },

    buildAddonsAndFeesEvent: function (): OrderEventItems | null {
      if (!ctrl.model.eventItems) {
        return null;
      }

      const items: TODO[] = [];

      for (const event of ctrl.model.eventItems as LooseObject<OrderEventItems>[]) {
        if (ctrl.isAddon(event) || ctrl.isFee(event)) {
          items.push(...(event.items || []));
        }
      }

      if (items.length === 0) {
        return null;
      }

      // Sort: addons/donations first, then fees
      items.sort((a, b) => {
        const aIsFee = a.itemType === 'Fee' ? 1 : 0;
        const bIsFee = b.itemType === 'Fee' ? 1 : 0;
        return aIsFee - bIsFee;
      });

      return {
        eventId: 'addons-fees',
        eventName: 'Add-ons, Donations & Fees',
        items: items,
        state: 'active',
        hasForms: false,
        hasResources: false,
      } as unknown as OrderEventItems;
    },

    updateNotes: function () {
      // noop
    },

    checkContacts: function (order) {
      let r = false;
      let billingContactInfo: TODO;
      let orderContactInfo: TODO;

      if (order) {
        billingContactInfo =
          (order.billingFirstName || '') +
          (order.billingLastName || '') +
          (order.billingEmail || '') +
          (order.billingPhoneNumber || '');
        orderContactInfo =
          (order.contactFirstName || '') +
          (order.contactLastName || '') +
          (order.email || '') +
          (order.phoneNumber || '');
        if (billingContactInfo === orderContactInfo) {
          r = true;
        }
      }

      return r;
    },

    changeOrg: function () {
      const changeOrgModal = $uibModal.open<TODO>({
        templateUrl: 'views/application/views/partners/modals/selectList/selectList.tpl.html',
        size: 'lg',
        controller: 'PartnersModalsSelectListController',
        resolve: {
          currentOrg: () => {
            return {
              id: ctrl.model.accountId,
              name: ctrl.model.accountName,
            };
          },
        },
      });

      changeOrgModal.result
        .then((org) => {
          ctrl.saveLastOrg();
          ctrl.acceptOrgChange(org);
        })
        .catch(() => {
          // noop
        });
    },

    acceptOrgChange: function (organization) {
      if (ctrl.model.accountId === organization.company.id) {
        return;
      }

      $uibModal
        .open<TODO>({
          templateUrl: 'views/application/views/orders/modals/changeOrg/changeOrg.tpl.html',
          size: 'md',
          controller: 'OrdersModalsChangeOrgController',
          resolve: {
            nextOrganization: () => organization,
            currentOrder: () => ctrl.model,
            safe: () => true,
          },
        })
        .result.then((contact) => {
          ctrl.model.accountId = organization.company.id;
          ctrl.model.accountName = organization.company.name;

          if (contact.firstName) {
            ctrl.model.contactFirstName = contact.firstName;
          }
          if (contact.lastName) {
            ctrl.model.contactLastName = contact.lastName;
          }
          if (contact.phoneNumber) {
            ctrl.model.phoneNumber = contact.phoneNumber;
          }
          if (contact.email) {
            ctrl.model.email = contact.email;
          }

          if (contact.type === 'new') {
            ctrl.addContact(contact);
          }
        })
        .catch(() => {
          ctrl.revertOrg();
        });
    },

    showMessage: function (props: { title: string; message: string }) {
      if (!props.message) {
        return $q.resolve();
      }

      return $uibModal
        .open<TODO>({
          templateUrl: 'views/application/views/reports/create/modals/showMessage.tpl.html',
          size: 'md',
          controller: 'ReportsCreateShowMessageModalController',
          resolve: {
            currentTitle: () => props.title,
            currentMessage: () => props.message,
            safe: () => true,
          },
        })
        .result.catch(() => {
          // noop
        });
    },

    saveLastOrg: function () {
      ctrl.lastOrg = {
        isAccount: ctrl.isOrganizationOrder(),
        accountId: ctrl.model.accountId,
        accountName: ctrl.model.accountName,
        email: ctrl.model.email,
        phoneNumber: ctrl.model.phoneNumber,
        contactFirstName: ctrl.model.contactFirstName,
        contactLastName: ctrl.model.contactLastName,
      };
    },

    addNewContact: function () {
      ctrl.saveLastOrg();
      ctrl.addContact();
    },

    addContact: function (suggestedContact?: TODO) {
      const addModal = $uibModal.open<TODO>({
        templateUrl: 'views/application/views/partners/create/modals/ManageSecondaryContactModal.tpl.html',
        size: 'lg',
        controller: 'ManageSecondaryContactModalController',
        resolve: {
          ValidateService: () => ValidateService,
          alertMessage: () => suggestedContact && 'The Order Contact must be a contact at the Organization.',
          organizationId: () => ctrl.model.accountId,
          organizationName: () => ctrl.model.accountName,
          contact: () => suggestedContact || null,
          address: () => null,
        },
      });

      addModal.result
        .then((contact) => {
          let props: TODO = {};
          //add the secondaryContact
          if (contact) {
            props = contact;
            props.organizationId = ctrl.model.accountId;

            ApiService.organizations.contacts
              .create(props)
              .then((response) => {
                const _contact = response.data.payload;

                ctrl.model.contactFirstName = _contact.firstName;
                ctrl.model.contactLastName = _contact.lastName;
                ctrl.model.phoneNumber = _contact.phoneNumber;
                ctrl.model.email = _contact.email;

                $scope.$broadcast('ContactsTypeahead::Refresh', { organizationId: ctrl.model.accountId });
              })
              .catch(() => {
                ctrl.showMessage({
                  title: 'Unable to create contact',
                  message: 'Failed to create contact for organization.',
                });
              });
          }
        })
        .catch(() => {
          ctrl.revertOrg();
        });
    },

    revertOrg: function () {
      ctrl.model.accountId = ctrl.lastOrg.accountId;
      ctrl.model.accountName = ctrl.lastOrg.accountName;
      ctrl.model.contactFirstName = ctrl.lastOrg.contactFirstName;
      ctrl.model.contactLastName = ctrl.lastOrg.contactLastName;
      ctrl.model.phoneNumber = ctrl.lastOrg.phoneNumber;
      ctrl.model.email = ctrl.lastOrg.email;
    },

    createPersonTypeKey: function (props: {
      eventId: string;
      itemTypeId: string;
      orderItemId: string | null;
    }): PersonTypeKey {
      if (props.orderItemId) {
        return `item-${props.eventId}-${props.itemTypeId}-${props.orderItemId}`;
      }

      return `item-${props.eventId}-${props.itemTypeId}`;
    },

    fromPersonTypeKey: function (key: PersonTypeKey): {
      eventId: string;
      itemTypeId: string;
      orderItemId: string | null;
    } {
      const parts = key.split('-');

      return {
        eventId: parts[1],
        itemTypeId: parts[2],
        orderItemId: parts[3] || null,
      };
    },

    /**
     * Apply formatted event dates to the order event.
     * Uses the pure formatEventDates function.
     */
    applyFormattedEventDates: function (orderEvent: ProcessedOrderEventItems): void {
      const formatted = ctrl.formatEventDates(orderEvent.eventDate, {
        dateFormat: ctrl.dateFormat,
        hourFormat: ctrl.hourFormat,
        parseDate: CalendarService.parseDate,
        toFormat: CalendarService.toFormat,
      });
      orderEvent.startDate = formatted.startDate;
      orderEvent.startHour = formatted.startHour;
    },

    /**
     * Apply event state flags to the order event.
     * Uses the pure processEventState function.
     */
    applyEventState: function (orderEvent: ProcessedOrderEventItems): void {
      const state = ctrl.processEventState(orderEvent.state, orderEvent.cancellationDate, {
        dateTimeFormat: HelpersService.dateFormats.standardDateTime,
        toFormat: CalendarService.toFormat,
      });
      orderEvent.isCancelled = state.isCancelled;
      orderEvent.isSuspended = state.isSuspended;
      orderEvent.formattedCancellationDate = state.formattedCancellationDate;
    },

    /**
     * Get the default email language from the available languages.
     * Uses the pure getDefaultEmailLanguage function.
     */
    getDefaultEmailLanguage: function (): EmailLanguage {
      return getDefaultEmailLanguage(ctrl.emailLanguages, DEFAULT_EMAIL_LANGUAGE_CODE);
    },

    /**
     * Process an individual event item to set up person types, sales channels, and discount flags.
     */
    processEventItem: function (
      eventItem: LooseObject<OrderEventItem>,
      orderEvent: ProcessedOrderEventItems,
      event: TODO,
    ): void {
      eventItem.resolvedQuantity = eventItem.resolvedQuantity || eventItem.quantity;
      eventItem.salesChannels = [];

      // Display tax-excluded (base) prices when sales tax is enabled, consistent
      // with the checkout and print views. Mutates display-only price fields.
      applyBasePriceDisplay(eventItem, ctrl.enableSalesTax);

      const key = ctrl.createPersonTypeKey({
        eventId: orderEvent.eventId,
        // TODO: b2bAddEventToOrder - find out why we sometimes use ticketTypeId and normalize to itemTypeId
        itemTypeId: eventItem.ticketTypeId || eventItem.itemTypeId,
        orderItemId: eventItem.orderItemId,
      });

      if (ctrl.isEventItemType(eventItem.itemType)) {
        eventItem.isDiscounted = ctrl.isDiscountedItem(eventItem);
        const eventPersonTypeIndex = ctrl.findEventPersonType(orderEvent.eventId, eventItem.itemTypeName);
        ctrl.personTypes[key] = event?.priceList?.prices?.[eventPersonTypeIndex] ?? [];
        eventItem.salesChannels = ctrl.personTypes[key].salesChannels;
      } else if (ctrl.isInventoryItemType(eventItem.itemType)) {
        if (ctrl.eventHasAddOns(orderEvent.eventId)) {
          const index = ctrl.findEventInventoryType(orderEvent.eventId, eventItem.itemTypeName);
          ctrl.personTypes[key] = event?.addOns?.inventoryAddOns?.[index];
          eventItem.salesChannels = ctrl.personTypes[key]?.salesChannels;
        } else {
          ctrl.personTypes[key] = ctrl.addons[eventItem.itemTypeId];
        }
      }

      return;
    },

    /**
     * Transform and enrich a raw Order object into a view-ready model.
     * Processes each event item with computed properties and metadata for display.
     */
    processOrder: function (order: Order) {
      if (!order.eventItems?.length) {
        order.preferredLanguage = ctrl.getDefaultEmailLanguage();
        return order;
      }

      let hasMembershipConversion = false;

      for (const orderEvent of order.eventItems as ProcessedOrderEventItems[]) {
        if (!ctrl.enableCSRToggle) {
          orderEvent.toggleCSRPrices = true;
          orderEvent.toggleCSRAddons = true;
        }

        ctrl.applyFormattedEventDates(orderEvent);
        ctrl.applyEventState(orderEvent);

        const event = ctrl.events[orderEvent.eventId];
        if (event) {
          attachMeta(orderEvent, {
            morePrices: ctrl.getRemainingPricesForEvent(event, orderEvent),
            moreAddOns: ctrl.getRemainingAddOnsForEvent(event, orderEvent),
          });
        }

        for (const eventItem of orderEvent.items as LooseObject<OrderEventItem>[]) {
          ctrl.processEventItem(eventItem, orderEvent, event);

          hasMembershipConversion = hasMembershipConversion || ctrl.isMembershipConversion(eventItem);
        }
      }

      ctrl.hasMembershipConversion = hasMembershipConversion;
      order.preferredLanguage = ctrl.getDefaultEmailLanguage();

      return order;
    },

    processOrderEvents: function (orderEvents: TODO[]) {
      for (const orderEvent of orderEvents) {
        if (!ctrl.events[orderEvent.id]) {
          ctrl.events[orderEvent.id] = orderEvent;
        }
      }
    },

    processSummary: function (data: OrderChargesResultVo) {
      const orderItemCharges = ensureArray(data.orderItemCharges);

      for (const orderItemCharge of orderItemCharges) {
        // TODO: b2bAddEventToOrder - dynamic property
        orderItemCharge['orderItem'] = orderItemCharge.orderItemId
          ? ctrl.findOrderItemById(orderItemCharge.orderItemId)
          : ctrl.findOrderItem({
              eventId: orderItemCharge.eventId!,
              itemTypeId: orderItemCharge.itemTypeId!,
            });
      }

      data.orderItemCharges = ctrl.sortByEventAndTicketIndex(orderItemCharges);

      return data;
    },

    eventHasItemsWithQuantity: function (event: OrderEventItems) {
      return event.items.some((item) => item.quantity > 0);
    },

    shouldDisplayEvent: function (event: OrderEventItems) {
      if (!ctrl.eventInOriginalOrder(event)) {
        return true;
      }

      const original = ctrl.originalOrder.eventItems.find((e) => e.eventId === event.eventId);
      return !!original && original.items.some((item) => item.quantity > 0);
    },

    eventHasAddOns: function (eventId: string) {
      return (
        ctrl.events[eventId] &&
        ctrl.events[eventId].hasOwnProperty('addOns') &&
        Array.isArray(ctrl.events[eventId].addOns?.inventoryAddOns)
      );
    },

    eventIsAddOn: function (eventId: string) {
      return (
        ctrl.events[eventId] &&
        ctrl.events[eventId].hasOwnProperty('addOns') &&
        Array.isArray(ctrl.events[eventId].addOns?.inventoryAddOns)
      );
    },

    sortByEventAndTicketIndex: function (list: TODO[]) {
      list.sort((a, b) => {
        const aOrderItemIndex = a.orderItem.index;
        const bOrderItemIndex = b.orderItem.index;
        const r = aOrderItemIndex.event - bOrderItemIndex.event;

        if (r !== 0) {
          return r;
        }

        return aOrderItemIndex.item - bOrderItemIndex.item;
      });

      return list;
    },

    findEventPersonType: function (eventId: string, type: string) {
      let r = 0;

      const eventPrices = ctrl.events[eventId].priceList?.prices ?? [];

      for (let i = 0, ii = eventPrices.length; i < ii; i++) {
        if (eventPrices[i].personType?.name === type) {
          r = i;
          break;
        }
      }

      return r;
    },

    findEventInventoryType: function (eventId: string, name: string) {
      let r = 0;

      if (
        ctrl.events[eventId] &&
        ctrl.events[eventId].hasOwnProperty('addOns') &&
        ctrl.events[eventId].addOns?.inventoryAddOns instanceof Array
      ) {
        for (let i = 0, ii = ctrl.events[eventId].addOns.inventoryAddOns.length; i < ii; i++) {
          if (ctrl.events[eventId].addOns.inventoryAddOns[i].name === name) {
            r = i;
            break;
          }
        }
      }

      return r;
    },

    findTicketDiscounted: function (eventId: string, ticketTypeId: string) {
      return resolveTicketDiscount({
        eventId,
        ticketTypeId,
        appliedCoupons: ctrl.appliedCoupons,
        eventCoupons: ctrl.eventCoupons,
        priceListPrices: ctrl.events[eventId]?.priceList?.prices,
      });
    },

    findItemTypeForEvent: function (eventItems: TODO[], itemTypeIdToFind: string, orderItemId: string | null) {
      for (let index = 0, ii = eventItems.length; index < ii; index++) {
        // intentionally using == here and not ===
        const item = eventItems[index];
        const itemTypeId = item.ticketTypeId || item.itemTypeId;

        if (orderItemId && item.orderItemId === orderItemId && !ctrl.isMembershipConversion(item)) {
          return { index, item };
        } else if (
          itemTypeId == itemTypeIdToFind &&
          (item.ticketAddedToOrder || !item.isDiscounted) &&
          !ctrl.isMembershipConversion(item)
        ) {
          return { index, item };
        }
      }

      return { item: null as TODO, index: -1 };
    },

    findEventById: function (list: WillCallOrderItemsByEventVo[], eventId: string) {
      return list.find((item) => item.eventId === eventId);
    },

    findOrderItemById: function (orderItemId: string) {
      for (let eventIndex = 0; eventIndex < ctrl.model.eventItems.length; eventIndex++) {
        const items = ctrl.model.eventItems[eventIndex].items;

        for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
          if (items[itemIndex].orderItemId === orderItemId) {
            return {
              item: items[itemIndex],
              index: { event: eventIndex, item: itemIndex },
            };
          }
        }
      }

      return undefined;
    },

    findOrderItem: function (props: { eventId: string; itemTypeId: string }) {
      for (let eventIndex = 0; eventIndex < ctrl.model.eventItems.length; eventIndex++) {
        if (ctrl.model.eventItems[eventIndex].eventId !== props.eventId) {
          continue;
        }

        const items = ctrl.model.eventItems[eventIndex].items;

        for (let itemIndex = 0; itemIndex < items.length; itemIndex++) {
          const itemTypeId = items[itemIndex].ticketTypeId || items[itemIndex].itemTypeId;

          if (itemTypeId == props.itemTypeId) {
            return {
              item: items[itemIndex],
              index: { event: eventIndex, item: itemIndex },
            };
          }
        }
      }

      return undefined;
    },

    hasPriceList: function (event: { priceList: PriceList }) {
      return event.priceList && Array.isArray(event.priceList.prices);
    },

    getEventCapacity: function (eventId: string) {
      const event = ctrl.events[eventId] ? ctrl.events[eventId] : null;

      if (event) {
        const capacity = event.capacity ?? 0;
        const soldQuantity = event.soldQuantity ?? 0;

        return capacity - soldQuantity;
      }

      return 0;
    },

    addTicketTypeForEvent: function (selectedItem: SelectedItemType, orderEvent: TODO) {
      if (typeof selectedItem !== 'object') {
        return;
      }

      const itemTypeId = selectedItem.personType.id!;
      const itemTypeName = selectedItem.personType.name!;
      const foundItem = ctrl.findItemTypeForEvent(orderEvent.items, itemTypeId, null);
      const itemIndex = ctrl.findItemTypeForEvent(orderEvent.items, itemTypeId, null).index;

      // TODO: get itemType from event instead of hard coding it here to Event
      if (itemIndex < 0 || foundItem.item.isDiscounted) {
        // new item

        const membershipId = ctrl.getMembershipIdFromOrder(ctrl.model);

        const accountCategoryId = ctrl.getAccountCategoryIdFromOrder(ctrl.model);

        const newItem = applyBasePriceDisplay(
          {
            discountedUnitPrice: selectedItem.discountedUnitPrice,
            itemType: 'Event',
            itemTypeId,
            itemTypeName: itemTypeName,
            quantity: 1,
            resolvedQuantity: 0,
            ticketTypeId: selectedItem.personType.id, // TODO: b2bAddEventToOrder - Legacy field for API compatibility
            ticketTypeName: itemTypeName, // TODO: b2bAddEventToOrder - Legacy field for API compatibility
            ticketTypeDescription: selectedItem.personType.description, // TODO: b2bAddEventToOrder - Legacy field for API compatibility
            unitPrice: selectedItem.price,
            basePrice: selectedItem.basePrice,
            discountedBasePrice: selectedItem.discountedBasePrice ?? selectedItem.basePrice,
            taxInclusiveUnitPrice: selectedItem.taxInclusiveUnitPrice,
            taxInclusiveDiscountedUnitPrice: selectedItem.taxInclusiveDiscountedUnitPrice,
            salesChannels: selectedItem.salesChannels,
            itemAddedToOrder: true,
            ...pickDiscountMeta(selectedItem),
            ...(membershipId && { membershipId: Number(membershipId) }),
            ...(accountCategoryId && { accountCategoryId }),
          },
          ctrl.enableSalesTax,
        );

        orderEvent.items.push(newItem);

        const eventPersonTypeIndex = ctrl.findEventPersonType(orderEvent.eventId, newItem.itemTypeName);
        const key = ctrl.createPersonTypeKey({
          eventId: orderEvent.eventId,
          itemTypeId: newItem.itemTypeId,
          orderItemId: null,
        });
        ctrl.personTypes[key] = ctrl.events[orderEvent.eventId].priceList?.prices?.[eventPersonTypeIndex];
      } else {
        //existing item - increment quantity
        ctrl.incrementItemTypeForEvent(orderEvent, itemIndex);
      }

      attachMeta(orderEvent, {
        morePrices: ctrl.getRemainingPricesForEvent(ctrl.events[orderEvent.eventId], orderEvent),
        moreAddOns: ctrl.getRemainingAddOnsForEvent(ctrl.events[orderEvent.eventId], orderEvent),
      });
    },

    addInventoryTypeForEvent: function (selectedItem: TODO, event: TODO) {
      if (typeof selectedItem !== 'object') {
        return;
      }

      const itemIndex = ctrl.findItemTypeForEvent(event.items, selectedItem.id, null).index;

      if (selectedItem) {
        // TODO: get itemType from event instead of hard coding it here to Event
        if (itemIndex < 0) {
          // new item
          const itemTypeId = selectedItem.id;
          const itemTypeName = selectedItem.name;

          event.items.push(
            applyBasePriceDisplay(
              {
                itemTypeId,
                itemType: 'Inventory',
                itemTypeName,
                quantity: 1,
                resolvedQuantity: 0,
                unitPrice: selectedItem.price,
                discountedUnitPrice: selectedItem.price,
                basePrice: selectedItem.basePrice,
                discountedBasePrice: selectedItem.basePrice,
                taxInclusiveUnitPrice: selectedItem.taxInclusiveUnitPrice,
                taxInclusiveDiscountedUnitPrice: selectedItem.taxInclusiveDiscountedUnitPrice,
                salesChannels: selectedItem.salesChannels,
              },
              ctrl.enableSalesTax,
            ),
          );

          const key = ctrl.createPersonTypeKey({
            eventId: event.eventId,
            itemTypeId,
            orderItemId: null,
          });
          const index = ctrl.findEventInventoryType(event.eventId, itemTypeName);
          ctrl.personTypes[key] = ctrl.events[event.eventId].addOns?.inventoryAddOns?.[index];
        } else {
          //existing item - increment quantity
          ctrl.incrementItemTypeForEvent(event, itemIndex);
        }
      }

      attachMeta(event, {
        morePrices: ctrl.getRemainingPricesForEvent(ctrl.events[event.eventId], event),
        moreAddOns: ctrl.getRemainingAddOnsForEvent(ctrl.events[event.eventId], event),
      });
    },

    incrementItemTypeForEvent: function (event, itemIndex) {
      const quantity = ctrl.normalizeQuantity(event.items[itemIndex].quantity);

      event.items[itemIndex].quantity = quantity + 1;
    },

    removeItemTypeForEvent: function (
      orderEvent: OrderEventItems & { toggleCSRPrices: boolean; toggleCSRAddons: boolean },
      $event: { event: TODO; item: TODO; itemIndex?: number },
    ) {
      const { item, itemIndex } = $event;

      // If itemIndex is provided, use it directly (more reliable for duplicate itemTypeIds)
      if (itemIndex !== undefined && itemIndex >= 0 && itemIndex < orderEvent.items.length) {
        const removedItem = orderEvent.items[itemIndex];
        orderEvent.items.splice(itemIndex, 1);

        const itemTypeId = removedItem.ticketTypeId || removedItem.itemTypeId;
        const key = ctrl.createPersonTypeKey({
          eventId: orderEvent.eventId,
          itemTypeId,
          orderItemId: removedItem.orderItemId,
        });

        if (ctrl.personTypes[key]) {
          delete ctrl.personTypes[key];
        }
      } else {
        // Use reference equality to find the exact item — avoids picking the wrong row
        // when multiple rows share the same ticketTypeId (e.g. original + newly-added rows).
        const refIndex = orderEvent.items.indexOf(item);

        if (refIndex === -1) {
          return;
        }

        orderEvent.items.splice(refIndex, 1);

        const itemTypeId = item.ticketTypeId || item.itemTypeId;
        const key = ctrl.createPersonTypeKey({
          eventId: orderEvent.eventId,
          itemTypeId,
          orderItemId: item.orderItemId,
        });

        if (ctrl.personTypes[key]) {
          delete ctrl.personTypes[key];
        }
      }

      attachMeta(orderEvent, {
        morePrices: ctrl.getRemainingPricesForEvent(ctrl.events[orderEvent.eventId], orderEvent),
        moreAddOns: ctrl.getRemainingAddOnsForEvent(ctrl.events[orderEvent.eventId], orderEvent),
      });
    },

    addFullPriceItem: function ($event: { event: WillCallOrderItemsByEventVo; item: TODO; itemIndex: number }) {
      const { event, item, itemIndex } = $event;

      // Find the event in the model
      const orderEvent = ctrl.model.eventItems.find((e) => e.eventId === event.eventId);

      if (!orderEvent || !orderEvent.eventId) {
        return;
      }

      // If any row (pre-existing or previously added) for this ticket type at full price
      // already exists, increment its quantity instead of adding a new row
      const existingItem = orderEvent.items.find(
        (i) => i.ticketTypeId === item.ticketTypeId && Number(i.discountedUnitPrice) === Number(item.unitPrice),
      );

      if (existingItem) {
        existingItem.quantity = ctrl.normalizeQuantity(existingItem.quantity ?? 0) + 1;
      } else {
        // Create a new item with the full price (unitPrice instead of discountedUnitPrice)
        const newItem = applyBasePriceDisplay(
          {
            discountedUnitPrice: item.unitPrice,
            itemType: item.itemType,
            itemTypeId: item.itemTypeId,
            itemTypeName: item.itemTypeName,
            quantity: 1,
            resolvedQuantity: 0,
            ticketTypeId: item.ticketTypeId,
            ticketTypeName: item.ticketTypeName,
            ticketTypeDescription: item.ticketTypeDescription,
            unitPrice: item.unitPrice,
            // Full price: the base equivalents both use the (undiscounted) base price,
            // and the tax-inclusive equivalents both use the (undiscounted) tax-inclusive price.
            basePrice: item.basePrice,
            discountedBasePrice: item.basePrice,
            taxInclusiveUnitPrice: item.taxInclusiveUnitPrice,
            taxInclusiveDiscountedUnitPrice: item.taxInclusiveUnitPrice,
            salesChannels: item.salesChannels,
            itemAddedToOrder: true,
          },
          ctrl.enableSalesTax,
        );

        // Insert the new item right after the current item (itemIndex + 1)
        orderEvent.items.splice(itemIndex + 1, 0, newItem);

        // Update personTypes if this is an event item
        if (newItem.itemType && ctrl.isEventItemType(newItem.itemType) && newItem.itemTypeName && newItem.itemTypeId) {
          const eventPersonTypeIndex = ctrl.findEventPersonType(orderEvent.eventId, newItem.itemTypeName);
          const key = ctrl.createPersonTypeKey({
            eventId: orderEvent.eventId,
            itemTypeId: newItem.itemTypeId,
            orderItemId: null,
          });
          if (eventPersonTypeIndex !== undefined) {
            ctrl.personTypes[key] = ctrl.events[orderEvent.eventId]?.priceList?.prices?.[eventPersonTypeIndex];
          }
        }
      }

      // Update metadata
      if (ctrl.events[orderEvent.eventId]) {
        attachMeta(orderEvent as TODO, {
          morePrices: ctrl.getRemainingPricesForEvent(ctrl.events[orderEvent.eventId], orderEvent as TODO),
          moreAddOns: ctrl.getRemainingAddOnsForEvent(ctrl.events[orderEvent.eventId], orderEvent as TODO),
        });
      }
    },

    addDiscountedPriceItem: function ($event: { event: WillCallOrderItemsByEventVo; item: TODO; itemIndex: number }) {
      const { event, item, itemIndex } = $event;

      // Find the event in the model
      const orderEvent = ctrl.model.eventItems.find((e) => e.eventId === event.eventId);

      if (!orderEvent || !orderEvent.eventId) {
        return;
      }

      // Get discount information for this ticket type
      const discountInfo =
        event.eventId && item.ticketTypeId ? ctrl.findTicketDiscounted(event.eventId, item.ticketTypeId) : null;

      if (!discountInfo) {
        // No discount available, don't add the item
        return;
      }

      // Get membership ID if available
      const membershipId = ctrl.getMembershipIdFromOrder(ctrl.model);

      const accountCategoryId = ctrl.getAccountCategoryIdFromOrder(ctrl.model);

      // Build the new discounted item first (with base-price normalization applied) so
      // duplicate detection compares against its final, normalized discountedUnitPrice —
      // existing rows are already normalized, so comparing on the raw discountInfo price
      // would miss matches when sales tax is enabled.
      const newItem = applyBasePriceDisplay(
        {
          discountedUnitPrice: discountInfo.discountedPrice,
          itemType: item.itemType,
          itemTypeId: item.itemTypeId,
          itemTypeName: item.itemTypeName,
          quantity: 1,
          resolvedQuantity: 0,
          ticketTypeId: item.ticketTypeId,
          ticketTypeName: item.ticketTypeName,
          ticketTypeDescription: item.ticketTypeDescription,
          unitPrice: item.unitPrice,
          basePrice: item.basePrice,
          discountedBasePrice: discountInfo.discountedBasePrice ?? discountInfo.discountedPrice,
          // Tax-inclusive: unit price keeps the item's full tax-inclusive price; the
          // discounted one is the discount's (tax-inclusive) discountedPrice.
          taxInclusiveUnitPrice: item.taxInclusiveUnitPrice,
          taxInclusiveDiscountedUnitPrice: discountInfo.discountedPrice,
          salesChannels: item.salesChannels,
          itemAddedToOrder: true,
          ...pickDiscountMeta(discountInfo),
          ...(membershipId && { membershipId: Number(membershipId) }),
          ...(accountCategoryId && { accountCategoryId }),
        },
        ctrl.enableSalesTax,
      );

      // If any row (pre-existing or previously added) for this ticket type at the discounted
      // price already exists, increment its quantity instead of adding a new row
      const existingDiscountedItem = orderEvent.items.find(
        (i) =>
          i.ticketTypeId === item.ticketTypeId &&
          Number(i.discountedUnitPrice) === Number(newItem.discountedUnitPrice),
      );

      if (existingDiscountedItem) {
        existingDiscountedItem.quantity = ctrl.normalizeQuantity(existingDiscountedItem.quantity ?? 0) + 1;
      } else {
        // Insert the new item right after the current item (itemIndex + 1)
        orderEvent.items.splice(itemIndex + 1, 0, newItem);

        // Update personTypes if this is an event item
        if (newItem.itemType && ctrl.isEventItemType(newItem.itemType) && newItem.itemTypeName && newItem.itemTypeId) {
          const eventPersonTypeIndex = ctrl.findEventPersonType(orderEvent.eventId, newItem.itemTypeName);
          const key = ctrl.createPersonTypeKey({
            eventId: orderEvent.eventId,
            itemTypeId: newItem.itemTypeId,
            orderItemId: null,
          });
          if (eventPersonTypeIndex !== undefined) {
            ctrl.personTypes[key] = ctrl.events[orderEvent.eventId]?.priceList?.prices?.[eventPersonTypeIndex];
          }
        }
      }

      // Update metadata
      if (ctrl.events[orderEvent.eventId]) {
        attachMeta(orderEvent as TODO, {
          morePrices: ctrl.getRemainingPricesForEvent(ctrl.events[orderEvent.eventId], orderEvent as TODO),
          moreAddOns: ctrl.getRemainingAddOnsForEvent(ctrl.events[orderEvent.eventId], orderEvent as TODO),
        });
      }
    },

    checkLimits: function (options?: { showSuccessMessage?: boolean }) {
      const showSuccessMessage = options?.showSuccessMessage ?? true;

      ctrl.model.orderItems = ctrl.buildOrderItems();

      const payload = OrderUpdateInputSchema.safeParse(ctrl.model);

      if (!payload.success) {
        ctrl.notice.showErrorMessages([{ message: 'Invalid order data' }]);
        return $q.reject({ valid: false });
      }

      ctrl.checkingLimits = true;

      return orderEditSalesRulesService
        .validateLimits(payload.data!, {
          onSuccess: (messages) => {
            if (showSuccessMessage) {
              ctrl.notice.showSuccessMessages(messages);
            }
          },
          onError: (messages) => ctrl.notice.showErrorMessages(messages),
          hideMessages: () => {
            ctrl.notice.hideErrorMessages();
            ctrl.notice.hideSuccessMessages();
          },
        })
        .then((response) => {
          // Store violations in service for banner display
          orderEditSalesRulesService.storeViolations(response);
          return response;
        })
        .finally(() => {
          ctrl.checkingLimits = false;
        });
    },

    getViolationsForEvent: function (eventId: string) {
      return orderEditSalesRulesService.getViolationsForEvent(eventId);
    },

    hasViolationsForEvent: function (eventId: string): boolean {
      return orderEditSalesRulesService.hasViolationsForEvent(eventId);
    },

    toggleLimitsOverride: function (eventId: string) {
      orderEditSalesRulesService.toggleOverride(eventId);
    },

    isLimitsOverrideActive: function (eventId: string): boolean {
      return orderEditSalesRulesService.isOverrideActive(eventId);
    },

    isLimitsChecked: function (eventId: string): boolean {
      return orderEditSalesRulesService.isLimitsChecked(eventId);
    },

    eventInOriginalOrder: function (event: OrderEventItems | WillCallOrderItemsByEventVo) {
      return ctrl.originalOrder.eventItems.some((e) => e.eventId === event.eventId);
    },

    removeEvent: function (event: OrderEventItems, index: number) {
      ctrl.model.eventItems.splice(index, 1);
      ctrl.refreshFormsFromEvents();
    },

    removeAllItemsForEvent: function (event: OrderEventItems & { showRestoreAllItems?: boolean }) {
      for (const item of event.items) {
        ctrl.removeItemQuantity({ item });
      }
      event.showRestoreAllItems = true;
    },

    removeItemQuantity: function (params: { item: OrderEditEventItem }) {
      const { item } = params;

      if (item.quantity) {
        item.showRestoreQuantity = true;
        item.originalQuantity = item.quantity;
        item.quantity = 0;
      }
      ctrl.refreshFormsFromEvents();
    },

    restoreAllItemsForEvent: function (event: OrderEventItems & { showRestoreAllItems?: boolean }) {
      for (const item of event.items) {
        ctrl.restoreItemQuantity({ item });
      }
      event.showRestoreAllItems = false;
    },

    restoreItemQuantity: function (params: { item: OrderEditEventItem }) {
      const { item } = params;
      if (!item.quantity) {
        item.showRestoreQuantity = false;
        item.quantity = item.originalQuantity || 0;
        item.originalQuantity = 0;
      }
      ctrl.refreshFormsFromEvents();
    },

    validateItem: function (item: { quantity: TODO; price: number; discountedUnitPrice: number }) {
      item.quantity = ctrl.normalizeQuantity(item.quantity);
      item.price = item.quantity * item.discountedUnitPrice;
    },

    calculatePrice: function (item: { quantity: string; price: TODO; discountedUnitPrice: number }) {
      if (item.quantity !== '') {
        ctrl.validateItem(item);
        return item.price;
      }

      return 0;
    },

    calculateAddOnPrice: function (addOn: { quantity: string | number; discountedUnitPrice: number }) {
      addOn.quantity = ctrl.normalizeQuantity(addOn.quantity);
      const price = Number(addOn.quantity) * addOn.discountedUnitPrice;

      return price || 0;
    },

    calculateOriginalSubtotal: function (
      item: { orderItemId?: string; resolvedQuantity?: string | number; discountedUnitPrice: number },
      eventId?: string,
      options?: { useTaxInclusive?: boolean },
    ) {
      return calculateOriginalSubtotal(ctrl.originalOrder, item.orderItemId, eventId, options);
    },

    /**
     * @deprecated
     */
    filterSaleChannel: function (items: TODO[], channel: 'customerRep' | '!customerRep') {
      return ($filter('saleChannel') as TODO)(items, channel) as TODO[];
    },

    resetNonCSRItems: function (event: { items: TODO[] }) {
      const nonCsrItems = ctrl.filterSaleChannel(event.items, '!customerRep');

      for (const item of nonCsrItems) {
        item.quantity = item.resolvedQuantity;
        item.showRestoreQuantity = false;
        item.originalQuantity = 0;
      }
    },

    togglePrices: function (event: { toggleCSRPrices: boolean; items: TODO[] }) {
      event.toggleCSRPrices = !event.toggleCSRPrices;

      if (!event.toggleCSRPrices) {
        ctrl.resetNonCSRItems(event);
      }
    },

    toggleAddons: function (event: { toggleCSRAddons: boolean; items: TODO[] }) {
      event.toggleCSRAddons = !event.toggleCSRAddons;

      if (!event.toggleCSRAddons) {
        ctrl.resetNonCSRItems(event);
      }
    },

    selectEmailOption: function () {
      if (ctrl.model.noEmail) {
        ctrl.emailOption = EmailOptions.None;
      } else if (ctrl.model.sendEmailToAll) {
        ctrl.emailOption = EmailOptions.AllRecipients;
      } else if (!ctrl.model.noEmail && !ctrl.model.sendEmailToAll) {
        ctrl.emailOption = EmailOptions.PrimaryOnly;
      }
    },

    setEmailOption: function () {
      switch (ctrl.emailOption) {
        case EmailOptions.None:
          ctrl.model.noEmail = true;
          ctrl.model.sendEmailToAll = false;
          break;
        case EmailOptions.PrimaryOnly:
          ctrl.model.noEmail = false;
          ctrl.model.sendEmailToAll = false;
          break;
        case EmailOptions.AllRecipients:
          ctrl.model.noEmail = false;
          ctrl.model.sendEmailToAll = true;
          break;
      }
    },

    updateQuantity: function ($event: { item: { quantity: number }; quantity: number }) {
      ctrl.notice.hideErrorMessages();
      $event.item.quantity = $event.quantity;
      ctrl.refreshFormsFromEvents();
    },

    onReservationSuccess: function ($event: {
      item: { quantity: number; resolvedQuantity: number; _reservedQuantity: number };
    }) {
      const { item } = $event;
      const _reserved = item.quantity - item.resolvedQuantity;
      item._reservedQuantity = _reserved < 0 ? 0 : _reserved;
      ctrl.notice.hideErrorMessages();
    },

    onReservationError: function (message) {
      ctrl.notice.showErrorMessages([{ message }]);
    },

    validateQuantity: function (item) {
      item.quantity = ctrl.normalizeQuantity(item.quantity);
    },

    updateEventItemDiscounts: function () {
      ctrl.model.eventItems = applyDiscountsToEventItems(ctrl.model.eventItems, {
        appliedCoupons: ctrl.appliedCoupons,
        eventCoupons: ctrl.eventCoupons,
        originalEventItems: ctrl.originalOrder.eventItems,
      });

      // Coupon application updates discountedUnitPrice with the regular (tax-inclusive)
      // discounted price; re-apply base-price display so items stay consistent under sales tax.
      for (const event of ctrl.model.eventItems) {
        for (const item of event.items) {
          applyBasePriceDisplay(item, ctrl.enableSalesTax);
        }
      }
    },

    getRemainingPricesForEvent: function (event: EventVo, orderEvent: OrderEventItems | WillCallOrderItemsByEventVo) {
      if (!event || !event.priceList || !event.priceList.prices) {
        return [];
      }

      const eventPrices = ensureArray(event.priceList?.prices);
      const remainingPrices = eventPrices.filter((price) => {
        return !orderEvent.items.some((item) => {
          const itemTypeId = item.ticketTypeId || item.itemTypeId;
          return itemTypeId == price.personType?.id;
        });
      });

      const membershipId = ctrl.getMembershipIdFromOrder(ctrl.model);

      const accountCategoryId = ctrl.getAccountCategoryIdFromOrder(ctrl.model);

      // TODO: b2bAddEventToOrder - are all the properties needed?
      return remainingPrices.map((item) => {
        const ticketDiscounted = item.discountId
          ? item
          : event.id && item.personType?.id
            ? ctrl.findTicketDiscounted(event.id, item.personType.id)
            : null;
        const mapped = applyBasePriceDisplay(
          {
            name: item.personType?.name,
            // Properties expected by eventTypeahead.tpl.html template
            ticketingTypeName: item.personType?.name,
            ticketingTypeDescription: item.personType?.description,
            price: item.price,
            discountedPrice: ticketDiscounted?.discountedPrice || item.discountedPrice,
            // Additional properties for order processing
            itemType: 'Event',
            itemTypeName: item.personType?.name,
            quantity: 0,
            resolvedQuantity: 0, // TODO: b2bAddEventToOrder - why do we have a resolvedQuantity?
            ticketTypeId: item.personType?.id,
            ticketTypeName: item.personType?.name,
            unitPrice: item.price,
            discountedUnitPrice: ticketDiscounted?.discountedPrice || item.discountedPrice,
            basePrice: item.basePrice,
            discountedBasePrice: ticketDiscounted?.discountedBasePrice ?? item.discountedBasePrice ?? item.basePrice,
            ...pickDiscountMeta(ticketDiscounted),
            personType: item.personType,
            salesChannels: item.salesChannels,
            ticketAddedToOrder: true,
            ...(membershipId && { membershipId: Number(membershipId) }),
            ...(accountCategoryId && { accountCategoryId }),
          },
          ctrl.enableSalesTax,
        );

        // The ticket-type selector dropdown displays `price` / `discountedPrice`; keep them
        // in sync with the normalized unit prices so it shows base prices under sales tax.
        mapped.price = mapped.unitPrice;
        mapped.discountedPrice = mapped.discountedUnitPrice;

        return mapped;
      });
    },

    // TODO: b2bAddEventToOrder - this is not used, can we remove it?
    getPricesForEvent: function (eventId: string, toggleCSRPrices: boolean) {
      const event = ctrl.events[eventId];

      if (!event) {
        return [];
      }

      const prices = event.priceList?.prices ?? [];

      if (!toggleCSRPrices) {
        return ctrl.filterSaleChannel(prices, 'customerRep');
      }

      return ctrl.filterSaleChannel(prices, 'customerRep').concat(ctrl.filterSaleChannel(prices, '!customerRep'));
    },

    getRemainingAddOnsForEvent: function (
      event: EventVo,
      orderEvent: WillCallOrderItemsByEventVo | (OrderEventItems & { toggleCSRAddons: boolean }),
    ) {
      const eventAddOns = ensureArray(event.addOns?.inventoryAddOns);
      const remainingAddOns = eventAddOns.filter((addOn) => {
        return !orderEvent.items.some((item) => {
          const itemTypeId = item.ticketTypeId || item.itemTypeId;
          return String(itemTypeId) === String(addOn.id);
        });
      });

      // The add-on selector dropdown displays `price`. Under sales tax, show the base
      // (tax-excluded) price to stay consistent. Return shallow copies so the cached
      // event add-ons are not mutated, and preserve the original tax-inclusive price so
      // the added order item can still contribute tax to the order total.
      const toDisplayPrice = (addOns: TODO[]): TODO[] =>
        ctrl.enableSalesTax
          ? addOns.map((addOn) =>
              addOn.basePrice != null
                ? {
                    ...addOn,
                    price: addOn.basePrice,
                    taxInclusiveUnitPrice: addOn.price,
                    taxInclusiveDiscountedUnitPrice: addOn.price,
                  }
                : addOn,
            )
          : addOns;

      if (!('toggleCSRAddons' in orderEvent && orderEvent.toggleCSRAddons)) {
        return toDisplayPrice(ctrl.filterSaleChannel(remainingAddOns, 'customerRep'));
      }

      return toDisplayPrice(
        ctrl
          .filterSaleChannel(remainingAddOns, 'customerRep')
          .concat(ctrl.filterSaleChannel(remainingAddOns, '!customerRep')),
      );
    },

    getIntegerAmount: function (amount) {
      const numAmount = currencyFloatToInteger(amount);
      return integerToCurrencyFloat(Math.abs(numAmount));
    },

    // Whether an item contributes to the order money totals.
    isTotalableItem: function (event: TODO, item: TODO): boolean {
      return (
        ctrl.isFee(event) ||
        (item.itemType && (ctrl.isInventoryItemType(item.itemType) || ctrl.isEventItemType(item.itemType)))
      );
    },

    // What the customer actually pays per unit.
    // For tax-exempt orders no tax is ever charged, so we use the base (tax-excluded)
    // discounted price — this keeps newly added items from adding tax to the totals.
    // Otherwise we use the tax-inclusive price, falling back to the (possibly
    // base-normalized) discountedUnitPrice when no tax-inclusive value was preserved
    // (e.g. sales tax disabled).
    getItemPayablePrice: function (item: TODO): number {
      if (ctrl.isTaxExempt) {
        return Number(item.discountedBasePrice ?? item.discountedUnitPrice ?? 0);
      }
      return Number(item.taxInclusiveDiscountedUnitPrice ?? item.discountedUnitPrice ?? 0);
    },

    /**
     * Computes the order money breakdown. Base values are tax-excluded (matching the
     * item rows); the total is tax-inclusive (what the customer pays). Mirrors the
     * checkout page's subtotal / discount / tax / total math.
     */
    getOrderTotals: function () {
      let baseTotal = 0;
      let discountedBaseTotal = 0;
      let total = 0;

      for (const event of ctrl.model.eventItems) {
        for (const item of event.items) {
          if (!ctrl.isTotalableItem(event, item)) {
            continue;
          }
          const quantity = ctrl.normalizeQuantity((item as TODO).quantity ?? 0);
          baseTotal += quantity * currencyFloatToInteger((item as TODO).basePrice ?? item.unitPrice ?? 0);
          discountedBaseTotal +=
            quantity * currencyFloatToInteger((item as TODO).discountedBasePrice ?? item.discountedUnitPrice ?? 0);
          total += quantity * currencyFloatToInteger(ctrl.getItemPayablePrice(item));
        }
      }

      return {
        subtotal: integerToCurrencyFloat(baseTotal),
        discount: integerToCurrencyFloat(baseTotal - discountedBaseTotal),
        tax: integerToCurrencyFloat(total - discountedBaseTotal),
        total: integerToCurrencyFloat(total),
      };
    },

    getCurrentTotal: function () {
      return ctrl.getOrderTotals().total;
    },

    getSubtotal: function () {
      return ctrl.getOrderTotals().subtotal;
    },

    getDiscountTotal: function () {
      return ctrl.getOrderTotals().discount;
    },

    getTax: function () {
      return ctrl.getOrderTotals().tax;
    },

    getBalanceDue: function () {
      const apiAmount = ctrl.summary?.subtotalPaymentAmount ?? null;

      if (apiAmount != null) {
        return parseFloat(String(apiAmount));
      }

      return parseFloat(String(ctrl.model.orderBalance)) + ctrl.getOrderDifference() || 0;
    },

    getSubtotalPaymentAmount: function () {
      const subtotalPaymentAmount = currencyFloatToInteger(ctrl.summary?.subtotalPaymentAmount ?? 0);

      return integerToCurrencyFloat(subtotalPaymentAmount);
    },

    getPreviousPayment: function () {
      let totalPrice = 0;

      // Only calculate from events that were in the original order
      for (const event of ctrl.model.eventItems) {
        // Skip newly added events - they shouldn't be in previous payment
        if (!ctrl.eventInOriginalOrder(event)) {
          continue;
        }

        for (const item of event.items) {
          if (ctrl.isTotalableItem(event, item)) {
            const quantity = ctrl.normalizeQuantity((item as TODO).resolvedQuantity ?? 0);
            // Previous payment is real (tax-inclusive) money.
            const unitPrice = ctrl.getItemPayablePrice(item);
            totalPrice = totalPrice + quantity * unitPrice;
          }
        }
      }

      return totalPrice - ctrl.model.orderBalance;
    },

    processComplete: function () {
      ctrl.model.orderItems = ctrl.buildOrderItems();

      ctrl.setEmailOption();

      if (ctrl.model.payment) {
        if (ctrl.model.payment.contactEmail) {
          ctrl.model.email = ctrl.model.payment.contactEmail;
        }

        if (ctrl.model.payment.contactFirstName) {
          ctrl.model.payment.firstName = ctrl.model.payment.contactFirstName;
        }

        if (ctrl.model.payment.contactLastName) {
          ctrl.model.payment.lastName = ctrl.model.payment.contactLastName;
        }

        if (ctrl.model.payment.address) {
          ctrl.model.address = ctrl.model.payment.address;
        }
      }
    },

    buildOrderItems: function () {
      ctrl.updateOrderItems();
      return Object.values(ctrl.orderItems);
    },

    buildEventItems: function () {
      return ctrl.model.eventItems.map((eventItem) => ({
        ...eventItem,
        items: eventItem.items.filter((item) => (item.quantity && item.quantity > 0) || item.orderItemId),
      }));
    },

    updateOrderItems: function () {
      ctrl.orderItems = {};
      let counter = 0;

      for (const orderEvent of ctrl.model.eventItems) {
        const eventId = orderEvent.eventId;

        if (!eventId) {
          continue;
        }

        for (const item of orderEvent.items as TODO[]) {
          if (!item.hasOwnProperty('quantity') || item.quantity < 0 || ctrl.isMembershipConversion(item)) {
            continue;
          }

          if (ctrl.isInventoryItemType(item.itemType) || ctrl.isEventItemType(item.itemType)) {
            const key = `item-${eventId}-${counter++}` as const;
            const itemTypeId = item.ticketTypeId || item.itemTypeId;

            ctrl.orderItems[key] = {
              orderItemId: item.orderItemId,
              quantity: item.quantity,
              eventId,
              itemType: item.itemType,
              itemTypeId,
              discountId: item.discountId || null,
              couponId: item.couponDetail?.id || null,
              accountCategoryId: item.accountCategoryId || null,
              membershipId: item.membershipId || null,
            };
          }
        }
      }
    },

    canSubmitOrder: function () {
      return hasAllEventItemsWithQuantity(ctrl.model.eventItems, ctrl.originalOrder.eventItems);
    },

    submitOrder: function () {
      ctrl.notice.hideSuccessMessages();
      ctrl.notice.hideErrorMessages();

      const proceed = () => {
        ctrl.isProcessing = true;
        ctrl.processComplete();

        // Validate limits before processing order
        ctrl
          .checkLimits({ showSuccessMessage: false })
          .then((response) => {
            if (!response.valid) {
              // Check if all violations have exceptions made
              if (!orderEditSalesRulesService.allViolationsHaveOverrides()) {
                // Validation failed and not all exceptions made - block submission
                ctrl.isProcessing = false;
                return;
              }
              // All violations have overrides - allow to proceed
            }

            // Validation passed or all exceptions made - continue with order processing
            ctrl
              .calculateOrderChargesFromAPI()
              .then((calcResponse) => {
                ctrl.summary = ctrl.processSummary(calcResponse);
                ctrl.processPayment();
              })
              .catch(() => {
                // TODO: b2bAddEventToOrder - handle errors and block submission until calculation succeeds
                ctrl.processPayment();
              });
          })
          .catch(() => {
            // Validation rejected (e.g., invalid payload)
            ctrl.isProcessing = false;
          });
      };

      // Validate that order has at least one item with quantity > 0
      if (!ctrl.canSubmitOrder()) {
        ctrl.notice.showErrorMessages([{ message: 'You must add tickets to the order.' }]);

        return;
      }

      if (!ctrl.validateRequiredForms()) {
        ctrl.notice.showErrorMessages([{ message: 'Please complete all required forms.' }]);

        return;
      }

      // Check for suspended events FIRST, before any processing
      if (!ctrl.hasItemsOnSuspendedEvents()) {
        proceed();
        return;
      }

      // Show warning modal for suspended events
      modalService
        .confirm({
          title: 'Suspended Event',
          message: 'You are editing an order for a suspended event, are you sure you want to continue?',
          okText: 'Proceed',
        })
        .then((confirmed) => {
          if (confirmed) {
            proceed();
          }
        });
    },

    calculateOrderChargesFromAPI: function () {
      ctrl.model.orderItems = ctrl.buildOrderItems();

      const payload = OrderUpdateInputSchema.safeParse(ctrl.model);

      return ApiService.orders.calculate(payload.data!).then(extractACMEPayload());
    },

    shouldShowPaymentForm: function () {
      return ctrl.getBalanceDue() > 0;
    },

    hasItemsOnSuspendedEvents: function () {
      return ctrl.model.eventItems.some((eventItem) => {
        return (
          eventItem.state === 'suspended' &&
          (eventItem.items as LooseObject<OrderEventItem>[]).some(
            (item) => typeof item.resolvedQuantity === 'number' && item.resolvedQuantity < item.quantity,
          )
        );
      });
    },

    processPayment: function () {
      ctrl.isProcessing = true;

      if (ctrl.getBalanceDue() > 0) {
        // Send message to payment directive to process order
        ctrl.validateEmail = ctrl.emailOption === EmailOptions.None ? false : true;
        $scope.$broadcast('Payment::Process', { validateEmail: ctrl.validateEmail });
        ctrl.processPaymentErrors();
      } else {
        ctrl.model.payment = null;
        ctrl.complete();
      }
    },

    clearLoading() {
      ctrl.isProcessing = false;
    },

    shouldUseCORSCheckout() {
      return CheckoutProcessService.shouldUseCORSCheckout({ requiresCardData: ctrl.requiresCardData });
    },

    complete: async function () {
      ctrl.processComplete();

      ctrl.notice.hideSuccessMessages();
      ctrl.notice.hideErrorMessages();

      /**
       * This is order duplication prevention
       * We create the checkout UUID and tack it onto the checkout request
       * Only rewrite the requestUUID when there are no errors and a checkout has taken place
       * This will always be refreshed when the page is refreshed, but, in the context of an order
       * it should only be overwritten if it has been explicitly set to `false`
       */
      if (!ctrl.requestUUID) {
        ctrl.requestUUID = uuid2.newuuid();
      }

      if (ctrl.selectedOrgContact) {
        ctrl.model.contactFirstName = ctrl.selectedOrgContact.firstName;
        ctrl.model.contactLastName = ctrl.selectedOrgContact.lastName;
        ctrl.model.phoneNumber = ctrl.selectedOrgContact.phoneNumber;
        ctrl.model.email = ctrl.selectedOrgContact.email;
        if (ctrl.selectedOrgContact.id) {
          ctrl.model.accountContactId = ctrl.selectedOrgContact.id;
        }
      }

      const formItems = ctrl.buildFormItemsForOrder();

      ctrl.model.giftCardAuthorizationIds = ctrl.giftCardRedemptions.map((r) => r.authorizationId);

      // Filter out items with no quantity and no orderItemId (i.e., newly added items that were not given a quantity)
      // This prevents sending items that are not in the original order and have no quantity to the API
      ctrl.model.eventItems = ctrl.buildEventItems();
      // Rebuild orderItems from the just-filtered eventItems so the update payload doesn't
      // still carry entries for items that were filtered out above.
      ctrl.model.orderItems = ctrl.buildOrderItems();

      if (ctrl.shouldUseCORSCheckout()) {
        const data: OrderEditData = CheckoutProcessService.processOrderUpdateData(
          { ...ctrl.model, requestUUID: ctrl.requestUUID, forms: formItems },
          { withCreditCardInfo: false },
        );

        ctrl.summary = await ctrl.calculateOrderChargesFromAPI().catch(() => null);
        data.payment.chargeAmount = ctrl.summary?.subtotalPaymentAmount ?? null;

        CheckoutProcessService.handleACMEPaymentSessionEditOrder(ctrl.processACMEPaymentData(data))
          .then((response) => ctrl.success.complete(response))
          .catch(async (response) => {
            if (isACMECORSCheckoutError(response)) {
              switch (response.type) {
                case 'sdk.render_failed': {
                  ctrl.clearLoading();

                  ctrl.error.complete({
                    status: 0,
                    data: { code: 0, errorCode: 0, error: response.params.message },
                  });
                  break;
                }

                case 'sdk.cancel_flow': {
                  ctrl.clearLoading();
                  break;
                }

                case 'sdk.billing_address.request_change': {
                  ctrl.clearLoading();
                  document.querySelector(`[data-testid="billing-address-anchor"]`)?.scrollIntoView();
                  break;
                }

                case 'api.error': {
                  ctrl.error.complete(CheckoutProcessService.mapToPaymentProcessError(response));
                  break;
                }

                default: {
                  console.warn(`unhandled`, response);

                  ctrl.clearLoading();
                  break;
                }
              }
            } else {
              ctrl.error.complete(response);
            }
          });
      } else {
        const data: OrderEditCoreData = CheckoutProcessService.processOrderUpdateCoreData({
          ...ctrl.model,
          requestUUID: ctrl.requestUUID,
          forms: formItems,
        });

        ApiService.orders
          .update(data)
          .then((response) => ctrl.success.complete(response.data.payload))
          .catch((response) => ctrl.error.complete(response));
      }
    },

    processACMEPaymentData: function (data: OrderEditData): SecureOrderEditProps {
      const countryData = LocationService.findCountry({ country: data.billingCountry });
      const stateData = LocationService.findState({ state: data.billingState });
      const cssVarsData = CheckoutProcessService.getCORSCheckoutCSSVariables();

      return {
        values: data,
        options: {
          formattedBillingAddress: addressService.formatAddress({
            streetAddress1: data.billingAddress1,
            streetAddress2: data.billingAddress2,
            country: countryData ? countryData.country.name : data.billingCountry,
            zipCode: data.billingZipCode,
            city: data.billingCity,
            state: stateData ? stateData.name : data.billingState,
          }),
          modal: {
            title: 'Payment',
            submit: 'Complete Order',
            close: 'Close',
          },
          authorizePayment: {
            paymentData: {
              externalId: '',
              chargeAmount: data.payment.chargeAmount ?? String(ctrl.getBalanceDue()),
              firstName: data.billingFirstName || '',
              lastName: data.billingLastName || '',
              creditCardVerificationData: {
                address1: data.billingAddress1,
                address2: data.billingAddress2,
                country: countryData ? String(countryData.country.numericCode) : data.billingCountry,
                zipCode: data.billingZipCode,
                city: data.billingCity,
                state: stateData ? stateData.abbreviation : data.billingState,
              },
            },
            saleChannel: ctrl.model.accountId ? 'reseller' : 'insideSalesIndividual',
          },
          capturePayment: {
            loaderHtml: CheckoutProcessService.getCheckoutLoadingHtml(),
          },
          autorenew: {
            enabled: false,
            html: ThemesHelperService.getTextVariable('checkout-process-page-autorenew-message'),
            url: '',
          },
          terms: {
            html: ThemesHelperService.getHTMLVariable('checkout-process-page-terms'),
            url: '',
          },
          creditCardSettings: {
            supportedCardsText: ThemesHelperService.getTextVariable('checkout-supported-cards-text'),
            showVisaLogo: ThemesHelperService.getTextVariable('checkout-show-visa-logo'),
            showMcLogo: ThemesHelperService.getTextVariable('checkout-show-mc-logo'),
            showDiscoverLogo: ThemesHelperService.getTextVariable('checkout-show-discover-logo'),
            showAmexLogo: ThemesHelperService.getTextVariable('checkout-show-amex-logo'),
          },
          variables: cssVarsData,
        },
      };
    },

    cancel: function () {
      window.history.go(-1);
    },

    /**
     * Called when user clicks "Keep editing" in the discard changes modal.
     * Dismisses the modal and stays on the current page.
     */
    keepEditing: function () {
      // No action needed - modal will be dismissed and user stays on page
      return;
    },

    /**
     * Called when user clicks "Discard changes" in the modal.
     * Continues navigation without saving by broadcasting promptSave::Complete.
     */
    discardChanges: function () {
      $scope.$broadcast('promptSave::Complete');
    },

    /**
     * Checks if the order has unsaved changes (used by promptSave directive).
     * Compares event item counts and quantities between model and original order.
     * If the order has been saved, always returns false.
     */
    checkDirty: function (): boolean {
      if (ctrl.orderSaveComplete) {
        return false;
      }

      const modelEvents = ctrl.model.eventItems || [];
      const originalEvents = ctrl.originalOrder.eventItems || [];

      // Check if event count changed
      if (modelEvents.length !== originalEvents.length) {
        return true;
      }

      // Check each event's items
      for (let i = 0; i < modelEvents.length; i++) {
        const modelEvent = modelEvents[i];
        const originalEvent = originalEvents.find((e) => e.eventId === modelEvent.eventId);

        // Event was added (not in original)
        if (!originalEvent) {
          return true;
        }

        const modelItems = modelEvent.items || [];
        const originalItems = originalEvent.items || [];

        // Check if item count changed
        if (modelItems.length !== originalItems.length) {
          return true;
        }

        // Check quantities on each item
        for (const modelItem of modelItems) {
          const itemTypeId = modelItem.ticketTypeId || modelItem.itemTypeId;
          const originalItem = originalItems.find((item) => {
            const origItemTypeId = item.ticketTypeId || item.itemTypeId;
            return origItemTypeId === itemTypeId && item.orderItemId === modelItem.orderItemId;
          });

          // Item was added (not in original)
          if (!originalItem) {
            return true;
          }

          // Check if quantity changed
          if (modelItem.quantity !== originalItem.quantity) {
            return true;
          }
        }
      }

      return false;
    },

    /**
     * Saves the order and broadcasts completion event for promptSave directive.
     * Called when user chooses "Save" in the prompt modal.
     */
    saveAndContinue: function () {
      // Store original handlers
      const originalSuccessComplete = ctrl.success.complete;

      // Override success handler to broadcast promptSave::Complete
      ctrl.success.complete = () => {
        $scope.$broadcast('promptSave::Complete');
        // Restore original handler
        ctrl.success.complete = originalSuccessComplete;
      };

      // Store original error handler
      const originalErrorComplete = ctrl.error.complete;

      // Override error handler to broadcast promptSave::Error
      ctrl.error.complete = (response) => {
        $scope.$broadcast('promptSave::Error');
        // Call original error handler
        originalErrorComplete(response);
        // Restore original handler
        ctrl.error.complete = originalErrorComplete;
      };

      // Trigger the existing submit flow
      ctrl.submitOrder();
    },

    processPaymentErrors: function () {
      const paymentErrors = ValidateService.getErrorHash();

      ctrl.hasEmailError = false;

      angular.forEach(paymentErrors, function (item, index) {
        if ((index as TODO) === 'payment-email') {
          ctrl.hasEmailError = true;
        }
      });
    },

    showEmailErrorMessage: function (emailOption: EmailOption) {
      return ctrl.hasEmailError && ctrl.emailOption === emailOption;
    },

    validateRequiredForms: function (): boolean {
      for (const form of ctrl.forms) {
        if (form.isRequired && !form.response) {
          return false;
        }
      }
      return true;
    },

    /**
     * Recomputes ctrl.forms from the current event/quantity state. Does NOT delete any
     * form responses itself (that used to happen here immediately) - it only records which
     * forms have dropped out of the order in ctrl.removedForms. Actual deletion is deferred to
     * removeResponsesForRemovedForms(), called once the order update succeeds, so a form isn't
     * deleted just because it disappeared for a moment (e.g. a quantity field being retyped).
     */
    refreshFormsFromEvents: function (): void {
      // Get all event forms from the current order
      const allEventForms = ctrl.getOrderFormsFromEvents();

      // Responses are read from cartFormsService (backed by sessionStorage) rather than the
      // outgoing ctrl.forms, so a response survives even if its form was pruned and re-added
      // across refreshes (e.g. a quantity field transiently hitting 0 while being retyped).
      const savedResponses = cartFormsService.getFormResponses();

      // Map all event forms and keep responses for forms that are still in the order
      const currentForms: OrderFormWithResponse[] = allEventForms.map((form) => {
        const savedResponse = savedResponses.find((response) => response.formUUID === form.uuid);
        return savedResponse ? { ...form, response: savedResponse } : form;
      });

      const currentUuids = new Set(currentForms.map((form) => form.uuid));

      // Forms that just dropped out of the order in this refresh
      const newlyRemovedForms = ctrl.forms.filter((form) => !currentUuids.has(form.uuid));

      // Accumulate removed forms across refreshes (dedupe by uuid), dropping any that have
      // since come back into the order (e.g. a removed item's quantity was restored) so their
      // responses aren't deleted on submit.
      const stillRemovedForms = ctrl.removedForms.filter((form) => !currentUuids.has(form.uuid));
      const stillRemovedUuids = new Set(stillRemovedForms.map((form) => form.uuid));
      ctrl.removedForms = stillRemovedForms.concat(
        newlyRemovedForms.filter((form) => !stillRemovedUuids.has(form.uuid)),
      );

      // Remove duplicate forms based on display settings
      ctrl.forms = deduplicateOrderFormsByDisplaySettings(currentForms);
    },

    removeResponsesForRemovedForms: function (): void {
      if (ctrl.removedForms.length) {
        cartFormsService.removeFormResponsesByFormIds(ctrl.removedForms.map((form) => form.id));
      }
      ctrl.removedForms = [];
    },

    getOrderFormsFromEvents: function (): OrderFormWithResponse[] {
      // Pre-build maps for O(1) lookups instead of O(n) find calls per event
      const originalEventItemByEventId = new Map(ctrl.originalOrder.eventItems.map((e) => [e.eventId, e]));
      const eventItemByEventId = new Map(ctrl.model.eventItems.map((e) => [e.eventId, e]));

      return Object.values(ctrl.events)
        .filter((event) => {
          const originalEventItem = event.id ? originalEventItemByEventId.get(event.id) : undefined;
          const eventItem = event.id ? eventItemByEventId.get(event.id) : undefined;

          // check if the event in the original order has been removed during edit mode (i.e., quantity of all items set to 0)
          if (
            originalEventItem &&
            eventItem &&
            originalEventItem.items.length === eventItem.items.length &&
            eventItem.items.every((item) => (item.quantity ?? 0) === 0)
          ) {
            return false;
          }

          return originalEventItem
            ? originalEventItem.items.some((item) => item.quantity && item.quantity > 0)
            : !!eventItem;
        })
        .flatMap((event) => {
          // compute per-event values once, shared across all forms of this event
          const eventItem = event.id ? eventItemByEventId.get(event.id) : undefined;
          const resolvedStartTime = eventItem?.eventDate || event.startTime;
          const displayStartDate =
            eventItem?.startDate ||
            CalendarService.toFormat(
              CalendarService.parseDate(event.startTime),
              UserPreferencesService.getDateFormat('shortDate', 'dateFormat'),
            );
          const displayStartTime =
            eventItem?.startHour ||
            CalendarService.toFormat(
              CalendarService.parseDate(event.startTime),
              UserPreferencesService.getDateFormat('standardTime', 'hourFormat'),
            );
          const displayEndTime = !eventItem
            ? CalendarService.toFormat(
                CalendarService.parseDate(event.endTime),
                UserPreferencesService.getDateFormat('standardTime', 'hourFormat'),
              )
            : '';

          const eventSummary = {
            id: event.id as string,
            name: event.name ?? '',
            startTime: resolvedStartTime || '',
            displayStartDate,
            displayStartTime,
            displayEndTime,
          };

          return (event.orderForms ?? []).map(
            (form): OrderFormWithResponse => ({
              ...(form as EventOrderForm),
              uuid: `${form.id!}-${event.id!}`, // form.id and event.id are guaranteed to be defined here
              response: null,
              isRequired: (form as EventOrderForm).preferences?.['insideSales'] === 'required',
              event: { ...eventSummary },
              itemType: 'event',
            }),
          );
        });
    },

    handleFormResponseChange: function (event: { form: CartOrderFormWithRequired; response: FormResponseRecord }) {
      const { form, response } = event;
      ctrl.forms = ctrl.forms.map((f) => {
        if (f.uuid === form.uuid) {
          return {
            ...f,
            response,
          };
        }
        return f;
      });
    },

    addEvent: function () {
      return $uibModal
        .open<EventVo[]>({
          templateUrl: 'views/application/views/orders/checkout/modals/addEvent.tpl.html',
          size: 'lg',
          controller: 'OrdersCheckoutAddEventModalController',
          resolve: {
            MODAL_DATA: () => {
              return {
                accountId: ctrl.model.accountId ? Number(ctrl.model.accountId) : null,
                membershipId: ctrl.getMembershipIdFromOrder(ctrl.model),
                startTime: new Date(),
                eventIds: ctrl.model.eventItems.map((item) => item.eventId).filter((id): id is string => !!id),
              } satisfies OrdersCheckoutAddEventModalData;
            },
          },
        })
        .result.then((events) => {
          ctrl.notice.hideSuccessMessages();
          ctrl.notice.hideErrorMessages();

          ctrl.processOrderEvents(events);
          let orderEventItems = events.map((event) =>
            ctrl.transformEventToWillCallOrderItemsByEvent(event, ctrl.enableSalesTax),
          );

          // Add membershipId to each ticket item if available
          const membershipId = ctrl.getMembershipIdFromOrder(ctrl.model);
          orderEventItems = ctrl.addMembershipIdToEventItems(orderEventItems, membershipId);

          // Add accountCategoryId to each ticket item if available
          const accountCategoryId = ctrl.getAccountCategoryIdFromOrder(ctrl.model);
          orderEventItems = ctrl.addAccountCategoryIdToEventItems(orderEventItems, accountCategoryId);

          ctrl.model.eventItems = ctrl.model.eventItems.concat(orderEventItems);
          ctrl.model = ctrl.processOrder(ctrl.model as TODO) as TODO;

          ctrl.fetchAvailableCoupons();
          ctrl.refreshFormsFromEvents();

          ctrl.getOrderAddonDetails().then(() => ctrl.initOrderAddonsData());
        })
        .catch(() => {
          // noop
        });
    },

    addPrivateEvent: function () {
      return $uibModal
        .open<TODO>(
          createPrivateEventModalConfig(() => {
            return {
              startTime: new Date(),
              accountId: ctrl.model.accountId ? Number(ctrl.model.accountId) : null,
              accountCategoryRelation: ctrl.accountCategoryRelation,
              preventAddWhenOutOfCapacity: true,
            };
          }),
        )
        .result.then((result) => {
          ctrl.notice.hideSuccessMessages();
          ctrl.notice.hideErrorMessages();

          const event = result.data.payload[0] as EventVo;
          ctrl.processOrderEvents([event]);
          const orderEventItem = ctrl.addAccountCategoryIdToEventItems(
            [transformEventToWillCallOrderItemsByEvent(event, ctrl.enableSalesTax)],
            ctrl.getAccountCategoryIdFromOrder(ctrl.model),
          )[0];
          ctrl.model.eventItems = ctrl.model.eventItems.concat(orderEventItem);
          ctrl.model = ctrl.processOrder(ctrl.model as TODO) as TODO;

          ctrl.refreshFormsFromEvents();

          ctrl.getOrderAddonDetails().then(() => ctrl.initOrderAddonsData());
        })
        .catch(() => {
          // noop
        });
    },

    buildFormItemsForOrder: function () {
      const order: Pick<OrderEditModel, 'orderId' | 'customerId'> = {
        orderId: ctrl.model.orderId,
        customerId: ctrl.model.customerId,
      };

      return buildFormItemsForOrder(ctrl.forms, ctrl.originalOrderFormUuids, order);
    },

    // utils
    isDiscountedItem,
    isMembershipConversion,
    isEventEditable,
    isAddon,
    isFee,
    isEventItemType,
    isInventoryItemType,
    normalizeQuantity,
    getMembershipIdFromOrder,
    getAccountCategoryIdFromOrder,
    addMembershipIdToEventItems,
    addAccountCategoryIdToEventItems,
    formatEventDates,
    processEventState,
    transformEventToWillCallOrderItemsByEvent,

    success: {
      getOrderAddonDetails: (response) => {
        // noop
      },

      complete: (response) => {
        const orderId = ctrl.model.orderId;

        // Only delete removed forms' responses once the update has actually succeeded, so a
        // failed submit (validation/API error) leaves them recoverable by restoring quantities.
        ctrl.removeResponsesForRemovedForms();

        if ($scope.stateTimer) {
          $timeout.cancel($scope.stateTimer);
        }

        ctrl.resetModel();
        ctrl.orderSaveComplete = true;

        $scope.$broadcast('Payment::Reset');
        $scope.stateTimer = $timeout(() => {
          $state.go('application.orders.detail.summary', { orderId });

          /**
           * State should have changed it is safe to restore on next tick
           *   requestUUID: false
           */
          // next tick
          $timeout(() => {
            ctrl.requestUUID = false;
          }, 0);
        }, 300);

        ctrl.isProcessing = false;
      },

      getDuplicateOrder: (response) => {
        ctrl.success.complete(response);
      },
    },

    error: {
      getOrderAddonDetails: (response) => {
        ctrl.notice.showErrorMessages([{ message: response.data.error }]);
      },

      complete: (response: { status: number; data: PaymentProcessError }) => {
        if (response.status === 409 && response.data.errorCode === 20152) {
          ctrl.getDuplicateOrder();
        } else {
          ctrl.isProcessing = false;

          $scope.$broadcast('Payment::Process::Error', { response: response });

          const messages = response.data.errors
            ? response.data.errors.map((error) => ({ message: error.message.replace(/^.*?:/, '') }))
            : [{ message: response.data.error.replace(/^.*?:/, '') }];

          ctrl.notice.showErrorMessages(messages);
        }
      },

      getDuplicateOrder: (response) => {
        ctrl.error.complete(response);
      },
    },

    onPaymentComplete: function (e, data) {
      ctrl.model.payment = data;
      CheckoutProcessService.setPaymentData(data);
      ctrl.complete();
    },

    onPaymentError: function (e, data) {
      ctrl.isProcessing = false;
      ctrl.notice.showErrorMessages([{ message: data }]);
    },

    onSelectedOrgContactUpdate: function (n, o) {
      if (ctrl.model && n) {
        ctrl.model.contactFirstName = n.firstName;
        ctrl.model.contactLastName = n.lastName;
        ctrl.model.phoneNumber = n.phoneNumber;
        ctrl.model.email = n.email;
        if (n.id) {
          ctrl.model.accountContactId = n.id;
        }
      }
    },

    onContactsTypeaheadNoResults: function (e, data) {
      // noop
    },

    onContactsTypeaheadContactNotFound: function (e, data) {
      // noop
    },
  };

  $scope.ordersEdit = ctrl;
  ctrl.init();
}

export const OrdersEditControllerModule = angular
  .module('acme.view.application.orders.edit.controllers', [
    uiRouter,
    'ui.bootstrap',
    'angularUUID2',
    'acme.common.service.api',
    'acme.common.service.order',
    'acme.common.service.calendar',
    'acme.common.service.permissions',
    'acme.common.service.helpers',
    'acme.common.service.validate',
    'acme.common.service.themesHelper',
    'acme.common.factory.notice',
    'acme.common.filter.saleChannel',
    'acme.common.service.userPreferences',
    OrdersModalsChangeOrgControllerModule,
    OrdersCheckoutAddEventModalControllerModule,
    CreatePrivateEventModalControllerModule,
    'acme.view.application.partners.modals.selectList.controllers',
    'acme.view.application.reports.create.showMessageModal.controllers',
    CheckoutProcessServiceModule,
    LocationServiceModule,
    PaymentServiceModule,
    AddressServiceModule,
    ModalServiceModule,
    TrustAsHtmlFilterModule,
    NoticesComponentModule,
    TenantPolicyTermsComponentModule,
    OrderEventHeaderComponentModule,
    ExistingOrderEventTableComponentModule,
    NewOrderEventTableComponentModule,
    ExistingOrderAddonsAndFeesComponentModule,
    AddonTypeSelectorComponentModule,
    TicketTypeSelectorComponentModule,
    OrderSummaryComponentModule,
    LimitsBannerComponentModule,
    ParseUrlFilterModule,
    'acme.common.directive.combobox',
    OrderEditSalesRulesServiceModule,
  ])
  .controller('OrdersEditController', OrdersEditController).name;
